import StageRequestProgress from "./components/StageRequestProgress";
import PartnerDirectoryDetails from "./components/PartnerDirectoryDetails";
import DeploymentFailures from "./components/DeploymentFailures";
import ReadModelInfo from "./components/ReadModelInfo";
import ReadModelEvidence from "./components/ReadModelEvidence";
// Stufenbranches in der Transportfreigabe (Vertrag 6, 14, 16.2): ersetzt bei eingeschaltetem Schalter den bisherigen
// Antrags- und Stufenpfad der Seite. Daten und Aktionen kommen aus useStageBoard, die Spalten aus PromotionStageBoard.
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, ButtonBase, Card, CardContent, CircularProgress, DialogActions, DialogContent, DialogTitle, LinearProgress, Snackbar, Stack, Tab, Tabs, TablePagination, TextField, Tooltip, Typography } from "@mui/material";
import { CicdMonitorButton, ExecutionErrors } from "../components/CicdExecution";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import StageReportDialog from "./components/StageReportDialog";
import StageArchiveDialog from "./components/StageArchiveDialog";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import RefreshRounded from "@mui/icons-material/RefreshRounded";
import { MonitorButton, MonitorDialog } from "../components/MonitorControls";
import PromotionStageBoard from "../components/PromotionStageBoard";
import PromotionSelectionBar from "../components/PromotionSelectionBar";
import useStageDrag from "../components/StageDrag";
import DateLabel from "../components/DateLabel";
import { formatDate, v } from "../utils/helpers";
import { stageLabel } from "../utils/stages";
import { NO_TOPIC, nextStage, regionsOf } from "./boardAdapter";
import { BOARD_READ_WINDOW_MS, newRequestId, useStageBoard } from "./useStageBoard";
import { requestPreparationNotices } from "./progressState";
import ProdLockControl from "./components/ProdLockControl";
import ReleaseSelectionDialog from "./components/ReleaseSelectionDialog";
import StageCellState, { CardFooter, FailedDot, AttentionDot, CardMetaTable } from "./components/StageCellState";
import { cicdMonitorUrl } from "../components/CicdExecution";
import PackageDetails, { EdgeHints, edgeManualArtifacts } from "./components/PackageDetails";
import PromotionArtifactDeployments from "../components/PromotionArtifactDeployments";
import { usePdConflicts, PdConflictSection } from "./components/PdConflictResolver";
import "./StageBranchApprovals.css";
import StageErrorAlert from "./components/StageErrorAlert";
import StageQueue from "./components/StageQueue";
import TopicFilter from "./components/TopicFilter";
import RegionFilter from "./components/RegionFilter";
import TargetPicker from "./components/TargetPicker";
import TargetValuesWarning, { baseStageLabel } from "./components/TargetValuesWarning";
import { initialTargets, isRollout, requestTargets, stageTargetList } from "./targetPreselect";
import { firstRunIdOf, targetRunsOf } from "../utils/stageTargets";
import TopicChangeControl from "./components/TopicChangeControl";

// Verfall optimistischer Overlays (DEV210/211): mindestens das längste Lesefenster eines Board-Abrufs, nie kürzer als 120 s.
const OVERLAY_TTL_MS = Math.max(120000, BOARD_READ_WINDOW_MS);

export function currentDetailRow(rows, selected) {
  if (!selected) return null;
  const exact = rows.find(row => row.id === selected.id);
  if (exact) return exact;
  const samePackage = rows.filter(row => row.packageId === selected.packageId);
  const sameTarget = samePackage.filter(row => row.targetId === selected.targetId);
  return sameTarget.length === 1 ? sameTarget[0] : samePackage.length === 1 ? samePackage[0] : null;
}

const stageOf_local = (data, stage) => (data?.stages || []).find(entry => entry.stage === stage) || null;

/**
 * Wiederholbarkeit einer Karte. Gemeinsame Regel fuer Karten-Icon und Details-Dialog, damit beide nie auseinanderlaufen.
 * Wiederholen nur mit canApprove; DISPATCH_REJECTED (Item failed ohne runId) ist wiederholbar, echtes not_started nicht;
 * STAGE_DISPATCH_UNKNOWN nur aus dem Antrags-/Itemstatus unknown (nicht aus pending-proof ohne Lauf).
 */
function retryActionOf(row, { board, requestStatusMap, rights, retryActive }) {
  const fwdReq = row.forward?.requestId ? (board?.requests || []).find(r => r.requestId === row.forward.requestId) : null;
  const fwdItem = fwdReq?.items?.find(it => it.packageId === row.packageId);
  const fwdItemStatus = fwdItem?.status || (fwdReq?.status === "failed" ? "failed" : fwdReq?.status === "not_started" ? "not_started" : null);
  const fwdItemNotStarted = fwdItemStatus === "not_started";
  // Die requestId steht bei pending-proof nicht in execution, sondern in openRequests des Board-Pakets.
  const boardPkg = (board?.packages || []).find(p => p.packageId === row.packageId);
  const cellReqId = row.execution?.requestId || (boardPkg?.openRequests || []).find(r => r.sourceStage === row.sourceStage || r.targetStage === row.sourceStage)?.requestId;
  const reqStatus = cellReqId ? requestStatusMap?.get(cellReqId) : null;
  const dispatchUnknown = reqStatus === "unknown";
  const importedPartial = row.stageState === "partial" && row.execution?.uploadConfirmed === true && !row.edgePending;
  const canRetryMain = !row.cellError && (["failed", "deployment-failed"].includes(row.stageState) || importedPartial) && !!row.execution?.requestId && rights?.canApprove === true && !dispatchUnknown && !retryActive;
  const canRetryFwd = !row.cellError && !!row.forward?.requestId && !fwdItemNotStarted && rights?.canApprove === true && !retryActive;
  return { fwdItemNotStarted, dispatchUnknown, canRetryMain, canRetryFwd };
}
const safeUrl_local = value => { try { return ["https:", "http:"].includes(new URL(value).protocol); } catch { return false; } };

/**
 * Details-Dialog einer Paketkarte mit ALLEN Aktionen (Vertrag „Ein Ziel, ein Bedienelement").
 * Die Karte selbst zeigt nur Titel, Info-Tabelle, Status und die Icon-Leiste;
 * jede aktive Aktion (Wiederholen, Edge-Sync, Nachziehen, PROD-Sperre) lebt hier.
 */
function CardDetailDialog({ row, board, rawBoard, spaceId, sb, readModelEnabled, onClose, onRetry, onRetryForward, onRollout, saveProdLock, rights, labelOf, instanceOf, stageErrorsOf, stageErrorText, retryActive, requestStatusMap }) {
  const { t } = useTranslation("freigabe");
  const panelId = useId();
  const [pending, setPending] = useState(false);
  const loadDetails = sb.loadDetails;
  // G9: Details bei JEDEM Oeffnen neu laden (nicht nur einmal je Sitzung).
  const [freshDetailsKey, setFreshDetailsKey] = useState(null);
  const detailScopeKey = JSON.stringify([spaceId, row.packageId]);
  useEffect(() => {
    let active = true;
    setFreshDetailsKey(null);
    Promise.resolve(loadDetails?.(row.packageId, { force: true })).then(() => { if (active) setFreshDetailsKey(detailScopeKey); });
    return () => { active = false; };
  }, [row.packageId, loadDetails, detailScopeKey]);
  const entry = sb.details?.[row.packageId];
  // PD-Konflikt (Vertrag v27): laden, wenn ein fehlgeschlagener Lauf vorliegt und Freigaberecht besteht.
  const failedRunId = ["failed", "partial", "deployment-failed"].includes(row.stageState) ? row.execution?.runId : row.forward?.runId;
  const pdConflict = usePdConflicts({ spaceId, packageId: row.packageId, runId: failedRunId, enabled: !!failedRunId && rights.canApprove === true });
  const { fwdItemNotStarted, dispatchUnknown, canRetryMain, canRetryFwd } = retryActionOf(row, { board, requestStatusMap, rights, retryActive });
  const canSync = row.stageState === "partial" && row.edgeSync?.state === "failed" && row.edgeSync?.canSync === true && (row.edgeSync?.partnerIds || []).length > 0;
  const edgeReason = row.stageState === "partial" && ["EDGE_FOLLOWUP_UNKNOWN", "EDGE_EVIDENCE_UNAVAILABLE"].includes(row.execution?.edgeReason)
    ? row.execution.edgeReason : null;
  const canRollout = !!row.rollout?.selectable;
  const showProdLock = !row.cellError && row.targetStage === "prod";
  const hasActions = canRetryMain || canRetryFwd || canSync || canRollout;
  // Laeufe und Artefakte aus den geladenen Details (HOCH 6: auch forward.stage).
  const stageEntry = stageOf_local(entry?.data, row.sourceStage);
  const fwdEntry = row.forward ? stageOf_local(entry?.data, row.forward.stage) : null;
  const runs = stageEntry?.runs || [];
  const fwdRuns = fwdEntry?.runs || [];
  const deploymentRun = runs.find(run => String(run.runId) === String(row.execution?.runId)
    && Number(run.runAttempt || 1) === Number(row.execution?.runAttempt || 1));
  const failedExecution = row.forward || row.execution;
  const failedRun = (row.forward ? fwdRuns : runs).find(run => String(run.runId) === String(failedExecution?.runId)
    && Number(run.runAttempt || 1) === Number(failedExecution?.runAttempt || 1));
  const undeployErrors = !retryActive && freshDetailsKey === detailScopeKey && !entry?.loading && !entry?.error
    && failedRun?.status === "completed" ? (failedRun.errorDetails || []).filter(error => error.code === "ARTIFACT_FAILED"
      && /:\s*undeploy$/.test(error.message || "") && String(error.runId) === String(failedRun.runId)
      && Number(error.runAttempt || 1) === Number(failedRun.runAttempt || 1)) : [];
  const result = run => t(run.status !== "completed" ? "stageBranches.details.result.running"
    : run.conclusion === "success" ? "stageBranches.details.result.success" : "stageBranches.details.result.failure");

  async function doAction(fn) {
    setPending(true);
    try { await fn(); } finally { setPending(false); }
  }

  return <MonitorDialog className="promotion-dialog sb-dialog" maxWidth="lg" aria-labelledby={panelId} onClose={() => { if (!pending) onClose(); }}>
    <DialogTitle id={panelId}>{t("stageBranches.details.toggle")}: {row.packageName || row.packageId}</DialogTitle>
    <DialogContent>
      <Stack spacing={2} sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ color: v("text-2"), overflowWrap: "anywhere" }}>{row.packageId} · {stageLabel(row.sourceStage)}</Typography>
        {entry?.data?.readModel && <ReadModelInfo label={t("stageBranches.readModel.infoLabel")}
          message={t(entry.data.readModel.state === "stale" ? "stageBranches.readModel.stale" : "stageBranches.readModel.infoReady")}
          metadata={entry.data.readModel}/>}
        {/* Aktionen oben, klar gruppiert */}
        {hasActions && <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {canRetryMain && <MonitorButton variant="contained" size="small" disabled={pending}
            onClick={() => doAction(() => onRetry(row))}>{pending ? t("promotion.sending") : t("stageBranches.cell.retry")}</MonitorButton>}
          {canRetryFwd && <MonitorButton variant="outlined" size="small" disabled={pending}
            onClick={() => doAction(() => onRetryForward(row))}>{t("stageBranches.cell.retry")} ({labelOf(board, row.forward.stage)})</MonitorButton>}
          {canSync && <StageCellState state="partial" execution={row.execution} edgeSync={row.edgeSync} packageId={row.packageId}
            spaceId={spaceId} instanceName={row.targetName || instanceOf(board, row.sourceStage) || undefined} pending={pending} pdConflict={pdConflict}/>}
          {canRollout && <MonitorButton variant="outlined" size="small" disabled={pending}
            onClick={() => doAction(() => onRollout(row))}>{t("stageBranches.card.rollout", { stage: labelOf(board, row.rollout.targetStage) })}</MonitorButton>}
        </Stack>}
        {!canSync && edgeReason && <Typography variant="body2" role="status" sx={{ color: v("text-1"), overflowWrap: "anywhere" }}>
          {t(`promotion.blocked.${edgeReason}`)}
        </Typography>}
        {/* Alle Warnungen und Hinweise, die frueher auf der Karte standen */}
        {row.forward && <Typography variant="body2" sx={{ color: v("text-2"), overflowWrap: "anywhere" }}>
          {t("stageBranches.card.forwardFailed", { stage: labelOf(board, row.forward.stage) })}</Typography>}
        {/* HOCH 5: Nicht gestartete Antraege: Hinweis statt Wiederholen */}
        {fwdItemNotStarted && row.forward && <Typography variant="body2" sx={{ color: v("warning"), overflowWrap: "anywhere" }}>
          {t("promotion.notStartedHint")}</Typography>}
        {/* Point 8: STAGE_DISPATCH_UNKNOWN: Hinweis mit Monitor-Link */}
        {dispatchUnknown && <Alert severity="warning" variant="outlined">
          {t("stageBranches.dispatchUnknown")}
          {row.execution?.requestId && <>{" "}<MonitorButton component="a" variant="text" size="small"
            href={cicdMonitorUrl({ spaceId, operationId: row.execution.requestId, packageId: row.packageId })}
            target="_blank" rel="noopener noreferrer">{t("stageBranches.cell.monitor")}</MonitorButton></>}
        </Alert>}
        {row.blockedReason && <Typography variant="body2" sx={{ color: v("text-2") }}>
          {t(`promotion.blocked.${row.blockedReason}`)}</Typography>}
        {(row.olderRequests || []).map(request => <Typography key={request.requestId} variant="body2" role="note" sx={{ color: v("warning"), overflowWrap: "anywhere" }}>
          {t("stageBranches.card.olderRequest", { source: labelOf(board, request.sourceStage), target: labelOf(board, request.targetStage) })}</Typography>)}
        {stageErrorsOf(row).map(entry => <Typography key={`${entry.stage}:${entry.code}`} variant="body2" sx={{ color: v("warning"), overflowWrap: "anywhere" }}>
          {stageErrorText(entry)}</Typography>)}
        {(row.referenceHints || []).map(hint => <Typography key={hint.id} variant="body2" role="status" sx={{ color: v("warning"), overflowWrap: "anywhere" }}>
          {hint.message}</Typography>)}
        {row.valuesMissing && <Typography variant="body2" role="status" sx={{ color: v("warning"), overflowWrap: "anywhere" }}>
          {t("stageBranches.targets.valuesMissing", { name: row.targetName || instanceOf(board, row.sourceStage) || row.targetId || "", stage: baseStageLabel(rawBoard) })}</Typography>}
        {row.edgeSkipped && <Typography variant="body2" role="status" sx={{ color: v("warning"), overflowWrap: "anywhere" }}>
          {t("stageBranches.warnings.EDGE_FOLLOWUP_SKIPPED")}</Typography>}
        <DeploymentFailures enabled={readModelEnabled && row.stageState === "deployment-failed"}
          failures={row.deploymentFailures} run={freshDetailsKey === detailScopeKey && !entry?.loading && !entry?.error ? deploymentRun : null}
          runtimeArtifactIds={(stageEntry?.artifacts || []).map(artifact => artifact.id)}
          scope={JSON.stringify([spaceId, row.packageId, row.sourceStage, row.targetId || "default"])}/>
        {row.stageState === "partial" && row.execution?.uploadConfirmed === true && !row.edgePending && <Alert severity="warning" variant="outlined">
          <Typography variant="body2">{t("stageBranches.details.importPartial")}</Typography>
          {(row.execution.importFailures || []).map((failure, index) => <Typography key={`${failure.artifactId}:${index}`} variant="body2" sx={{ overflowWrap: "anywhere" }}>
            {failure.artifactId}: {t(`stageBranches.details.importPhase.${failure.phase}`, { defaultValue: t("stageBranches.details.importPhase.unknown") })}
          </Typography>)}
        </Alert>}
        <ExecutionErrors details={undeployErrors} monitor={{ spaceId, runId: failedRun?.runId, packageId: row.packageId }}/>
        {/* PD-Konflikt */}
        <PdConflictSection pdConflict={pdConflict} hideLoading={!entry || entry.loading}
          targetStageLabel={row.stageState === "deployment-failed" ? labelOf(board, row.sourceStage) : ["failed", "partial"].includes(row.stageState) ? labelOf(board, row.targetStage) : row.forward ? labelOf(board, row.forward.stage) : null}
          stageLabelFn={key => labelOf(board, key)}/>
        {/* PROD-Sperre */}
        {showProdLock && <ProdLockControl item={row} spaceId={spaceId} canManage={rights.canManagePackages === true} onSave={saveProdLock}/>}
        {/* Edge-Hinweise */}
        {/* EIN Ladebalken fuer Details, Laeufe und Edge-Hinweise (alle aus derselben Ladung). */}
        {(!entry || entry.loading) && <Stack spacing={0.5}>
          <Typography variant="body2" sx={{ color: v("text-2") }}>{t("stageBranches.details.loading")}</Typography>
          <LinearProgress aria-label={t("stageBranches.details.loading")}/>
        </Stack>}
        <EdgeHints details={sb.details || {}} packageIds={[row.packageId]} stage={row.sourceStage} onRetry={loadDetails} hideLoading/>
        {entry?.error && <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
          <Typography variant="body2" sx={{ color: v("warning"), minWidth: 0 }}>{t("stageBranches.details.unavailable")}</Typography>
          <MonitorButton size="small" variant="text" onClick={() => loadDetails?.(row.packageId)}>{t("stageBranches.details.retry")}</MonitorButton>
        </Stack>}
        {entry?.data && <>
          {edgeManualArtifacts({ data: entry.data }, row.sourceStage).map(artifact => <Typography key={artifact.id} variant="body2" sx={{ color: v("text-1"), overflowWrap: "anywhere" }}>
            {t("stageBranches.details.edgeHint", { name: artifact.name || artifact.id })}</Typography>)}
          <Typography component="h3" variant="body2" sx={{ color: v("text-2"), fontWeight: 600, m: 0 }}>{t("stageBranches.details.runs")}</Typography>
          {runs.length ? <Stack component="ul" spacing={0.5} sx={{ listStyle: "none", m: 0, p: 0 }}>
            {runs.map(run => <Typography component="li" variant="body2" key={`${run.runId}:${run.runAttempt}`} sx={{ color: v("text-1"), overflowWrap: "anywhere" }}>
              {safeUrl_local(run.runUrl) ? <a href={run.runUrl} target="_blank" rel="noopener noreferrer" style={{ color: v("accent") }}>
                {t("stageBranches.details.run", { runId: run.runId, stage: stageLabel(row.sourceStage) })}</a>
                : t("stageBranches.details.run", { runId: run.runId, stage: stageLabel(row.sourceStage) })}
              {" · "}{result(run)}{run.completedAt && <>{" · "}<DateLabel value={run.completedAt}/></>}
            </Typography>)}
          </Stack> : <Typography variant="body2" sx={{ color: v("text-2") }}>{t("stageBranches.details.noRuns")}</Typography>}
          {/* HOCH 6: Laeufe der Folgestufe (forward) */}
          {fwdRuns.length > 0 && <>
            <Typography component="h3" variant="body2" sx={{ color: v("text-2"), fontWeight: 600, m: 0, mt: 1 }}>{t("stageBranches.details.runs")} ({stageLabel(row.forward.stage)})</Typography>
            <Stack component="ul" spacing={0.5} sx={{ listStyle: "none", m: 0, p: 0 }}>
              {fwdRuns.map(run => <Typography component="li" variant="body2" key={`fwd:${run.runId}:${run.runAttempt}`} sx={{ color: v("text-1"), overflowWrap: "anywhere" }}>
                {safeUrl_local(run.runUrl) ? <a href={run.runUrl} target="_blank" rel="noopener noreferrer" style={{ color: v("accent") }}>
                  {t("stageBranches.details.run", { runId: run.runId, stage: stageLabel(row.forward.stage) })}</a>
                  : t("stageBranches.details.run", { runId: run.runId, stage: stageLabel(row.forward.stage) })}
                {" · "}{result(run)}{run.completedAt && <>{" · "}<DateLabel value={run.completedAt}/></>}
              </Typography>)}
            </Stack>
          </>}
          <Typography component="h3" variant="subtitle2">{t("promotion.artifacts")}</Typography>
          <PromotionArtifactDeployments artifacts={stageEntry?.artifacts || []}
            artifactIds={stageEntry?.artifacts?.map(a => a.id) || row.artifactIds}
            designArtifacts={stageEntry?.designArtifacts}/>
          <PartnerDirectoryDetails data={stageEntry?.partnerDirectory}/>
        </>}
      </Stack>
    </DialogContent>
    <DialogActions><MonitorButton autoFocus onClick={onClose}>{t("gemeinsam:aktion.schliessen")}</MonitorButton></DialogActions>
  </MonitorDialog>;
}

const NOTE_MAX = 500;
const validNote = value => value.trim().length >= 3 && value.length <= NOTE_MAX;
const REQUEST_STATUS = new Set(["pending", "queued", "running", "unknown"]);
// details und status bleiben erhalten: Texte mit Platzhaltern (z. B. {{seconds}}) und Listen betroffener Ziele brauchen sie.
const asError = failure => ({ code: failure?.code || null, message: failure?.message || "", status: failure?.status ?? null,
  ...(failure?.details || failure?.body?.details ? { details: failure.details || failure.body.details } : {}) });
// Ein Antrag geht nach einer verlorenen Antwort mit derselben ID erneut hinaus; eine fachliche Ablehnung beendet ihn.
const rejected = failure => failure?.status >= 400 && failure.status < 500 && ![408, 429].includes(failure.status);

const matchesTopic = (pkg, topicId) => topicId === null || topicId === undefined ? true
  : topicId === NO_TOPIC ? !pkg.topicId : pkg.topicId === topicId;
const labelOf = (board, key) => board?.stages?.find(stage => stage.key === key)?.label || stageLabel(key);
const instanceOf = (board, key) => board?.stages?.find(stage => stage.key === key)?.instanceName || null;
const titleOf = item => item.packageName || item.packageId;
// Mehrzielmodus: dasselbe Paket kann in mehreren Zielkarten einer Stufe gewählt sein; Antrag und Liste führen es einmal.
const uniqueRows = rows => [...new Map(rows.map(row => [row.packageId, row])).values()];

// Offene Anträge aus board.packages[].openRequests, je requestId zusammengefasst (Vertrag 6.1).
function openRequestsOf(board, topicId) {
  if (Array.isArray(board?.requests)) return board.requests.map(request => ({ ...request,
    packages: (request.items || []).map(item => ({ ...item, topicId: board.packages.find(pkg => pkg.packageId === item.packageId)?.topicId ?? request.topicId })) }))
    .filter(request => !topicId || request.packages.some(pkg => matchesTopic(pkg, topicId)))
    .sort((a, b) => (b.requestedAt || "").localeCompare(a.requestedAt || ""));
  const byId = new Map();
  for (const pkg of board?.packages || []) {
    if (!matchesTopic(pkg, topicId)) continue;
    for (const open of pkg.openRequests || []) {
      if (!REQUEST_STATUS.has(open.status)) continue;
      const row = byId.get(open.requestId) || { ...open, approvable: true, packages: [], conflicts: [] };
      row.packages.push(pkg);
      row.approvable = row.approvable && open.approvable === true;
      for (const conflict of open.conflicts || []) {
        if (!row.conflicts.some(known => known.path === conflict.path && known.targetStage === conflict.targetStage)) row.conflicts.push(conflict);
      }
      byId.set(open.requestId, row);
    }
  }
  return [...byId.values()];
}

const requestCategory = status => REQUEST_STATUS.has(status) ? "open" : status === "completed" ? "successful"
  : ["failed", "partial", "not_started"].includes(status) ? "failed" : "all";

// Pakete, deren Umfang auf einer Stufe nicht bestimmbar ist (cell.error): als eigene Zeile in der Spalte, das Board bleibt
// vollständig (Vertrag 6.2, SB-R09-g). Die Zeile ist nie auswählbar.
function errorRowsOf(board, topicId, includeDev) {
  const stages = (board?.stages || []).filter(stage => stage.configured && (includeDev || stage.key !== "dev"));
  return (board?.packages || []).filter(pkg => matchesTopic(pkg, topicId)).flatMap(pkg => stages.flatMap(stage => {
    const key = stage.key;
    // Mehrzielmodus: Fehler je gezeigtem Ziel (Untergruppe), sonst der Fehler der Stufe wie bisher.
    const perTarget = stage.targets?.length > 1 && pkg.targets?.[key]
      ? stage.targets.map(target => ({ target, cell: pkg.targets[key][target.targetId] })).filter(entry => entry.cell) : null;
    // Fehlt `pkg.targets[key]`, gilt die Zelle der Stufe (Standardzielgruppe) als Rückfall.
    const cells = perTarget?.length ? perTarget : (pkg.cells?.[key] ? [{ target: stage.targets?.length > 1 ? (stage.targets.find(item => item.isDefault) || stage.targets[0]) : null, cell: pkg.cells[key] }] : []);
    // Widersprechen sich die Zellen (Fehler nur an der Zelle der Stufe), zeigt die Standardzielgruppe den Fehler.
    const standard = stage.targets?.length > 1 ? (stage.targets.find(item => item.isDefault) || stage.targets[0]) : null;
    const withCell = standard && pkg.cells?.[key]?.error && !cells.some(entry => entry.target?.targetId === standard.targetId && entry.cell.error)
      ? [...cells, { target: standard, cell: pkg.cells[key] }] : cells;
    return withCell.filter(({ cell }) => cell.error).map(({ target, cell }) => ({
      id: `error:${pkg.packageId}:${key}${target ? `:${target.targetId}` : ""}`, packageId: pkg.packageId, packageName: pkg.packageName,
      artifactIds: pkg.artifactIds || [], sourceStage: key, targetStage: nextStage(board, key), selectable: false, cellError: cell.error,
      ...(target ? { targetId: target.targetId } : {}), ...(pkg.transport === "package" ? { transport: "package" } : {}) }));
  }));
}

function RouteText({ board, sourceStage, targetStage }) {
  const { t } = useTranslation("freigabe");
  const side = key => `${instanceOf(board, key) || t("promotion.instanceMissing")} (${labelOf(board, key)})`;
  return <Typography component="span" variant="body2" sx={{ display: "block", color: v("text-2"), overflowWrap: "anywhere" }}>{side(sourceStage)} → {side(targetStage)}</Typography>;
}

// Instanzname eines Ziels aus dem Board (alle Ziele der Stufe, unabhängig von der Region); ohne Treffer null.
const targetNameIn = board => (stage, targetId) => {
  const rows = (board?.stages || []).filter(row => !stage || row.key === stage).flatMap(row => row.allTargets || row.targets || []);
  return rows.find(target => target.targetId === targetId)?.instanceName || null;
};
// Gesamtstand eines Ziels über die Pakete des Antrags: der kritischste Stand zuerst.
const TARGET_STATUS_ORDER = ["failed", "unknown", "partial", "running", "queued", "pending", "not_started", "completed"];
const worstStatus = statuses => TARGET_STATUS_ORDER.find(status => statuses.includes(status)) || statuses[0] || "pending";

// Ziele eines Antrags mit Schema 2 (nur Mehrzielmodus): Namen aus den Zielen der Zielstufe, sonst die IDs. Nach der
// Freigabe steht je Ziel der Stand seiner Läufe (Monitor Schema 2, requests[].items[].targets).
function RequestTargets({ board, item }) {
  const { t } = useTranslation("freigabe");
  if (!Array.isArray(item.targets) || !item.targets.length) return null;
  const nameOf = targetNameIn(board);
  const names = item.targets.map(id => nameOf(item.targetStage, id) || id);
  const runs = item.status === "pending" ? [] : item.targets.map(id => ({ id, statuses: (item.items || [])
    .flatMap(entry => targetRunsOf(entry, item.targets).filter(run => run.targetId === id).map(run => run.status)).filter(Boolean) }))
    .filter(row => row.statuses.length);
  const statusText = status => t(`promotion.status.${status}`, { defaultValue: status });
  return <>
    <Typography component="span" variant="body2" sx={{ display: "block", color: v("text-2"), overflowWrap: "anywhere" }}>
      {t("stageBranches.targets.of", { names: names.join(", ") })}</Typography>
    {runs.length > 0 && <Stack component="ul" spacing={0.25} sx={{ listStyle: "none", m: 0, p: 0 }} aria-label={t("stageBranches.targets.title")}>
      {runs.map(row => <Typography component="li" variant="body2" key={row.id} sx={{ color: v("text-1"), overflowWrap: "anywhere" }}>
        {t("stageBranches.targets.runLine", { name: nameOf(item.targetStage, row.id) || row.id, status: statusText(worstStatus(row.statuses)) })}</Typography>)}
    </Stack>}
  </>;
}

function RequestNote({ note, compact = false }) {
  const { t } = useTranslation("freigabe");
  if (typeof note !== "string" || !note.trim()) return null;
  return <Typography component="span" variant="body2" sx={{ display: "block", color: v("text-1"), mt: compact ? 1 : 0, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
    <Typography component="span" variant="body2" sx={{ display: "block", fontWeight: 600 }}>{t("promotion.requestNote")}</Typography>
    {note}
  </Typography>;
}

// Antrag für die Weitergabe der Auswahl (Vertrag 27.2): die gewählten Pakete als Liste, ohne zweite Auswahl, dazu die
// Edge-Hinweise der Quellstufe; bei verlorener Antwort dieselbe requestId.
export function RequestDialog({ board, rawBoard = board, compose, topicId, region, details, onLoadDetails, onSubmit, onClose, onLoadReferenceHints }) {
  const { t } = useTranslation(["freigabe", "gemeinsam"]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const draft = useRef(null);
  const rows = compose.rows;
  const edgePending = rows.some(row => row.edgePending);
  const ready = rows.length > 0 && validNote(note) && !busy;
  // Mehrzielmodus: Zielliste der Zielstufe, vorbelegt aus Wizard, Region oder Standardziel (je Paket einmal).
  const uniqueIds = [...new Set(rows.map(row => row.packageId))];
  const targetList = stageTargetList(rawBoard, compose.targetStage);
  const [preset] = useState(() => initialTargets({ board: rawBoard, targetStage: compose.targetStage, sourceStage: compose.sourceStage, packageIds: uniqueIds, region }));
  const [targets, setTargets] = useState(preset?.ids ?? null);
  const rollout = isRollout({ board: rawBoard, sourceStage: compose.sourceStage, targetStage: compose.targetStage, packageIds: uniqueIds });
  // Snapshot hints use the saved package facts. Target inventory is not inferred.
  // Failures remain visible; closing the dialog cancels its read.
  const [fetchedHints, setFetchedHints] = useState({ loading: false, hints: [], error: false });
  const [hintRetry, setHintRetry] = useState(0);
  const hintTargets = requestTargets(targetList, targets);
  const hintKey = JSON.stringify([uniqueIds, compose.sourceStage, compose.targetStage, hintTargets ?? null,
    uniqueIds.map(id => rawBoard?.packages?.find(pkg => pkg.packageId === id)?.cells?.[compose.sourceStage])]);
  useEffect(() => {
    if (!onLoadReferenceHints || !uniqueIds.length) return undefined;
    const controller = new AbortController();
    setFetchedHints({ loading: true, hints: [], error: false });
    Promise.resolve(onLoadReferenceHints({ packageIds: uniqueIds, sourceStage: compose.sourceStage, targetStage: compose.targetStage,
      targetIds: hintTargets || undefined, signal: controller.signal }))
      .then(result => {
        if (controller.signal.aborted) return;
        const byPackage = result?.referenceHints && typeof result.referenceHints === "object" ? result.referenceHints : {};
        setFetchedHints({ loading: false, error: result?.available === false, stored: result?.source === "snapshot",
          scopeHints: result?.scopeHints || {}, hints: referenceHintsForRequest({ packages: Object.entries(byPackage).map(([packageId, referenceHints]) => ({
          packageId, packageName: (rawBoard?.packages || []).find(pkg => pkg.packageId === packageId)?.packageName, referenceHints })) }, uniqueIds, compose.targetStage) });
      })
      .catch(() => { if (!controller.signal.aborted) setFetchedHints({ loading: false, hints: [], error: true }); });
    return () => controller.abort();
  }, [hintKey, hintRetry, onLoadReferenceHints]); // eslint-disable-line react-hooks/exhaustive-deps
  const requestReferenceHints = [...new Map([...referenceHintsForRequest(rawBoard, uniqueIds, compose.targetStage), ...fetchedHints.hints]
    .map(hint => [`${hint.packageId}:${hint.id || hint.referenceId}`, hint])).values()];

  async function submit() {
    if (!ready) return;
    const packageIds = uniqueIds, text = note.trim();
    const chosen = requestTargets(targetList, targets);
    const key = JSON.stringify([packageIds, text, chosen ?? null]);
    if (draft.current?.key !== key) draft.current = { key, requestId: newRequestId() };
    setBusy(true); setError(null);
    try {
      await onSubmit({ requestId: draft.current.requestId, sourceStage: compose.sourceStage, targetStage: compose.targetStage,
        packageIds, note: text, ...(topicId && topicId !== NO_TOPIC ? { topicId } : {}), ...(chosen ? { targets: chosen } : {}) });
    } catch (failure) {
      if (rejected(failure)) draft.current = null;
      setError(asError(failure));
      setBusy(false);
    }
  }

  const titleId = useId();
  return <MonitorDialog className="promotion-dialog sb-dialog" maxWidth="sm" aria-labelledby={titleId} onClose={() => { if (!busy) onClose(); }}>
    <DialogTitle id={titleId}>{t("promotion.requestDialogTitle", { stage: labelOf(board, compose.targetStage) })}</DialogTitle>
    <DialogContent>
      <Stack spacing={2}>
        <RouteText board={board} sourceStage={compose.sourceStage} targetStage={compose.targetStage}/>
        <Stack component="section" spacing={0.5} aria-label={t("stageBranches.page.packages")}>
          <Typography component="h3" variant="subtitle2" sx={{ color: v("text-1"), m: 0 }}>{t("stageBranches.page.packages")}</Typography>
          <Stack component="ul" spacing={0.5} sx={{ listStyle: "none", m: 0, p: 0 }}>
            {uniqueRows(rows).map(row => <Typography component="li" variant="body2" key={row.packageId} sx={{ color: v("text-1"), overflowWrap: "anywhere" }}>
              <strong>{titleOf(row)}</strong>{" "}<Typography component="span" variant="body2" sx={{ color: v("text-2") }}>
                {t("promotion.artifactCount", { count: (row.artifactIds || []).length })}{row.transport === "package" ? ` · ${t("promotion.wholePackage")}` : ""}</Typography></Typography>)}
          </Stack>
        </Stack>
        {targetList && targets && <TargetPicker targets={targetList} value={targets} onChange={setTargets} reason={preset?.reason} disabled={busy}/>}
        {targetList && <TargetValuesWarning board={rawBoard} sourceStage={compose.sourceStage} targetStage={compose.targetStage} packageIds={uniqueIds} targetIds={targets}/>}
        {rollout && targetList && <Alert severity="info" variant="outlined"><strong>{t("stageBranches.targets.rollout")}</strong>{" "}
          {t("stageBranches.targets.rolloutHint", { stage: labelOf(board, compose.targetStage) })}</Alert>}
        {edgePending && <Alert severity="info" variant="outlined">{t("stageBranches.hints.EDGE_PENDING")}</Alert>}
        {/* DEV183 I4: Hinweise zu fremden Referenzen der gewählten Pakete, ohne Sperre. */}
        {fetchedHints.loading && <Typography variant="body2" role="status" sx={{ color: v("text-2"), display: "inline-flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={14} aria-hidden="true" sx={{ color: "var(--iio-text-3)" }}/>{t("stageBranches.referenceHintsLoading")}</Typography>}
        {fetchedHints.error && <Alert severity="warning" variant="outlined" action={<MonitorButton size="small" onClick={() => setHintRetry(n => n + 1)}>{t("stageBranches.details.retry")}</MonitorButton>}>{t("stageBranches.referenceHintsUnavailable")}</Alert>}
        {fetchedHints.stored && <Typography variant="body2" sx={{ color: v("text-2") }}>{t("stageBranches.referenceHintsStored")}</Typography>}
        {Object.entries(fetchedHints.scopeHints || {}).map(([packageId, hints]) => hints.length > 0 && <Alert key={packageId} severity="warning" variant="outlined">{uniqueIds.length > 1 && <strong>{rawBoard?.packages?.find(p => p.packageId === packageId)?.packageName || packageId}</strong>}<Stack component="ul" sx={{ m: 0, pl: 2 }}>{hints.map((hint, i) => <li key={i}>{t(`stageBranches.warnings.scope.${hint.kind}`, { name: hint.name })}</li>)}</Stack></Alert>)}
        {/* Quelle: pkg.referenceHints der Zielstufe dieses Antrags, auch ohne offenen Antrag, ergänzt um die Prüfung beim Öffnen. */}
        {requestReferenceHints.length > 0 && <Alert severity="warning" variant="outlined">
          <strong>{t("stageBranches.referenceHints")}</strong>
          <Stack component="ul" spacing={0.25} sx={{ m: 0, mt: 0.5, pl: 2 }}>
            {requestReferenceHints.map(hint => <li key={`${hint.packageId}:${hint.id}`} style={{ overflowWrap: "anywhere" }}>{hint.name}: {hint.message}</li>)}
          </Stack></Alert>}
        <EdgeHints details={details} packageIds={uniqueIds} stage={compose.sourceStage} targetStage={compose.targetStage} onRetry={id => onLoadDetails(id, { force: true, sourceStage: compose.sourceStage })}/>
        <TextField multiline minRows={2} fullWidth required disabled={busy} value={note} label={t("promotion.requestNote")}
          inputProps={{ maxLength: NOTE_MAX }} error={note.length > 0 && !validNote(note)}
          helperText={t("promotion.noteHint", { count: note.length })} onChange={event => setNote(event.target.value)}/>
        <StageErrorAlert error={error} title={t("promotion.actionError")} targetName={targetNameIn(rawBoard)}/>
      </Stack>
    </DialogContent>
    <DialogActions>
      <MonitorButton autoFocus disabled={busy} onClick={onClose}>{t("gemeinsam:aktion.abbrechen")}</MonitorButton>
      <MonitorButton variant="contained" disabled={!ready} onClick={submit}>{t(busy ? "promotion.sending" : "promotion.request")}</MonitorButton>
    </DialogActions>
  </MonitorDialog>;
}

// Texte der Topic-Hinweise aus der Antwort von „Topic ändern“ (Korrekturrunde 2): umgehängt bzw. reserviert durch einen Auftrag.
export function topicNoticesOf(result, topics, t) {
  const nameOf = id => topics.find(topic => topic.id === id)?.name || id || "";
  return (Array.isArray(result?.warnings) ? result.warnings : []).filter(w => w?.code === "STAGE_TOPIC_LOCKED")
    .flatMap(w => (Array.isArray(w.packages) && w.packages.length ? w.packages : [w]).map(p => w.reason === "snapshot-queued"
      ? t("stageBranches.topicChange.queued", { packageId: p.packageId })
      : t("stageBranches.topicChange.moved", { packageId: p.packageId, oldTopic: p.topicName || nameOf(p.topicId),
        newTopic: nameOf(result?.assignments?.[p.packageId]) })));
}

// Hinweise eines Antrags als Warnung (vorhandenes Muster, keine Pills), je Code ein eigener Text in der Sprache der
// Oberfläche (Korrekturrunde 1 M1, M2); ohne eigenen Text die Servermeldung. Der Konflikt geteilter Ressourcen steht eigens.
export function RequestWarnings({ board, item, warnings }) {
  const { t, i18n } = useTranslation("freigabe");
  const rows = (Array.isArray(warnings) ? warnings : []).filter(w => w?.code && w.code !== "STAGE_SHARED_RESOURCE_CONFLICT");
  if (!rows.length) return null;
  const nameOf = targetNameIn(board);
  const textOf = w => {
    if (w.code === "STAGE_SCOPE_HINT") return (w.hints || []).map(h => t(`stageBranches.warnings.scope.${h.kind || "usage-unreadable"}`,
      { name: h.name || "", defaultValue: t("stageBranches.warnings.scope.usage-unreadable", { name: h.name || "" }) })).join(" ");
    if (w.code === "STAGE_TARGET_VALUES_MISSING") return t("stageBranches.warnings.STAGE_TARGET_VALUES_MISSING",
      { names: (w.targets || []).map(id => nameOf(item?.targetStage, id) || id).join(", "), stage: baseStageLabel(board) });
    if (w.code === "STAGE_TOPIC_LOCKED") return t("stageBranches.warnings.STAGE_TOPIC_LOCKED", { topicName: w.topicName || "" });
    if (w.code === "PD_SCOPE_UNKNOWN") return t("stageBranches.warnings.PD_SCOPE_UNKNOWN", { pid: w.pid || "" });
    const key = `stageBranches.warnings.${w.code}`;
    if (i18n.exists(`freigabe:${key}`)) return t(key);
    if (/^EDGE_/.test(w.code)) return t("stageBranches.warnings.EDGE");
    return w.message || t("stageBranches.warnings.generic");
  };
  return <Alert severity="warning" variant="outlined" role="status">
    <Stack component="ul" spacing={0.25} sx={{ m: 0, pl: 2 }}>
      {rows.map((w, i) => <li key={`${w.code}:${w.packageId || ""}:${w.targetId || ""}:${i}`} style={{ overflowWrap: "anywhere" }}>
        {w.packageId ? `${w.packageId}${w.targetId ? ` (${nameOf(item?.targetStage, w.targetId) || w.targetId})` : ""}: ` : ""}{textOf(w)}</li>)}
    </Stack>
  </Alert>;
}

// Snackbar im Stil der Karten und Dialoge, in hell und dunkel über die App-Variablen.
export const SNACKBAR_SX = { backgroundColor: "var(--iio-bg-1)", backgroundImage: "none", color: "var(--iio-text-1)",
  border: "1px solid var(--iio-border)", borderRadius: "8px", boxShadow: "var(--iio-shadow-md)", fontFamily: "inherit" };

// DEV183: Hinweise zu fremden Referenzen (Lane C). Sie stehen im Antrag unter warnings[] und am Board-Paket unter
// openRequests[].referenceWarnings; beide Quellen werden ohne Doppelung zusammengeführt. Sie sperren nie.
const REFERENCE_CODES = new Set(["STAGE_REFERENCE_NOT_ARRIVED", "STAGE_REFERENCE_UNCHECKED"]);
const referenceWarningsOf = warnings => (Array.isArray(warnings) ? warnings : []).filter(w => REFERENCE_CODES.has(w?.code));
const warningKey = w => [w.code, w.packageId, w.referenceId, w.targetInstanceId, w.targetStage].join("|");
export function mergeWarnings(...lists) {
  return [...new Map(lists.flatMap(list => (Array.isArray(list) ? list : [])).filter(w => w?.code).map(w => [REFERENCE_CODES.has(w.code) ? warningKey(w) : Symbol(), w])).values()];
}
/** Referenzwarnungen aus der Antwort von POST …/stage-requests: die Route liefert { request }. */
export const requestReferenceWarnings = result => referenceWarningsOf(result?.request?.warnings ?? result?.warnings);

/** pkg.referenceHints der gewählten Pakete für die Zielstufe des Antrags (Lane C, DEV183). */
export function referenceHintsForRequest(board, packageIds, targetStage) {
  const ids = new Set(packageIds || []);
  return [...new Map((board?.packages || []).filter(pkg => ids.has(pkg.packageId)).flatMap(pkg => (Array.isArray(pkg.referenceHints) ? pkg.referenceHints : [])
    .filter(hint => hint && typeof hint.message === "string" && hint.message.trim() && (!hint.targetStage || hint.targetStage === targetStage))
    .map(hint => [`${pkg.packageId}:${hint.id || hint.referenceId}`, { ...hint, packageId: pkg.packageId, name: pkg.packageName || pkg.packageId }]))).values()];
}
export function referenceWarningsOfRequest(board, item) {
  if (!item) return [];
  return (board?.packages || []).flatMap(pkg => (pkg.openRequests || []).filter(request => request.requestId === item.requestId)
    .flatMap(request => referenceWarningsOf(request.referenceWarnings).map(w => ({ packageId: pkg.packageId, ...w }))));
}

// Entscheidung über einen offenen Antrag: Freigeben mit pinDigest (Vertrag 4.1) oder Ablehnen mit Begründung.
// Blocking requests named by a failed preparation (STAGE_REQUEST_ALREADY_OPEN carries their ids). The error is the state
// at failure time; the current state of each request comes from the board, so a request closed since then says so.
function BlockingRequests({ preparation, requests, onOpen }) {
  const { t } = useTranslation("freigabe");
  const ids = preparation.error?.code === "STAGE_REQUEST_ALREADY_OPEN" && Array.isArray(preparation.error.details?.requestIds)
    ? preparation.error.details.requestIds : [];
  if (!ids.length) return null;
  const found = ids.map(id => requests.find(item => item.requestId === id)).filter(Boolean);
  const open = found.filter(item => requestCategory(item.status) === "open");
  return <Stack spacing={0.75} sx={{ mt: 1 }}>
    {open.map(item => <Stack key={item.requestId} direction="row" useFlexGap flexWrap="wrap" spacing={1} alignItems="center">
      <Typography variant="body2" sx={{ color: "var(--text-2)", overflowWrap: "anywhere" }}>
        {t("stageBranches.progress.blockingRequest", { packages: item.packages.map(titleOf).join(", "), status: t(`promotion.status.${item.status}`) })}
      </Typography>
      <MonitorButton size="small" variant="text" aria-haspopup="dialog" onClick={() => onOpen(item)}>{t("stageBranches.progress.openBlockingRequest")}</MonitorButton>
    </Stack>)}
    {!open.length && <Typography variant="body2" sx={{ color: "var(--text-2)", overflowWrap: "anywhere" }}>
      {t("stageBranches.progress.blockingRequestClosed")}</Typography>}
  </Stack>;
}

function DecisionDialog({ board, item, preparation, canApprove, details, onLoadDetails, onApprove, onReject, onViewRelease, onClose, referenceWarnings = [] }) {
  const { t } = useTranslation(["freigabe", "gemeinsam"]);
  // Der Freigebende sieht die Edge-Hinweise des weitergegebenen Inhalts, also der Quellstufe (Vertrag 27.3).
  const packageIds = item.packages.map(pkg => pkg.packageId);
  const packageKey = packageIds.join(",");
  useEffect(() => { packageKey.split(",").filter(Boolean).forEach(id => onLoadDetails?.(id, { sourceStage: item.sourceStage })); }, [packageKey, item.sourceStage, onLoadDetails]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const decide = canApprove && item.status === "pending" && !["preparing", "completed"].includes(preparation?.status);
  const run = async action => {
    setBusy(true); setError(null);
    try { await action(); }
    catch (failure) { setError(asError(failure)); setBusy(false); }
  };
  const titleId = useId();
  return <MonitorDialog className="promotion-dialog sb-dialog" maxWidth="sm" aria-labelledby={titleId} onClose={() => { if (!busy) onClose(); }}>
    <DialogTitle id={titleId}>{item.release?.tag ? t("stageBranches.release.titleRequest", { tag: item.release.tag }) : t("promotion.detailsTitle")}</DialogTitle>
    <DialogContent>
      <Stack spacing={2}>
        <Typography variant="body2" sx={{ color: v("text-1"), fontWeight: 600 }}>{t(item.status === "completed" ? "promotion.categories.successful" : `promotion.status.${item.status}`)}</Typography>
        {/* Automatisch ersetzt (Ein-Karten-Regel): sichtbarer Grund im Verlauf. */}
        {item.status === "superseded" && item.supersession && <Typography variant="body2" role="note" sx={{ color: v("text-2"), overflowWrap: "anywhere" }}>
          {(item.supersession.packages || []).some(entry => entry.snapshotAt)
            ? t("stageBranches.page.supersededSnapshot", { at: formatDate((item.supersession.packages || []).find(entry => entry.snapshotAt).snapshotAt) })
            : t("stageBranches.page.supersededReason", { stages: [...new Set((item.supersession.packages || []).map(entry => labelOf(board, entry.stage)))].join(", ") })}
        </Typography>}
        <StageRequestProgress request={item} preparation={preparation}/>
        <RouteText board={board} sourceStage={item.sourceStage} targetStage={item.targetStage}/>
        <RequestTargets board={board} item={item}/>
        <RequestNote note={item.note}/>
        <Stack component="section" spacing={0.5} aria-labelledby={`${titleId}-packages`}>
          <Typography id={`${titleId}-packages`} component="h3" variant="subtitle2" sx={{ color: v("text-1") }}>{t("stageBranches.page.packages")}</Typography>
          <Stack component="ul" spacing={0.5} sx={{ listStyle: "none", m: 0, p: 0 }}>
            {item.packages.map(pkg => <Typography component="li" variant="body2" key={pkg.packageId} sx={{ color: v("text-1"), overflowWrap: "anywhere" }}>{titleOf(pkg)}</Typography>)}
          </Stack>
        </Stack>
        <EdgeHints details={details} packageIds={packageIds} stage={item.sourceStage} targetStage={item.targetStage} onRetry={id => onLoadDetails(id, { force: true, sourceStage: item.sourceStage })}/>
        {/* Hinweise des Antrags (Vorprüfung, Umfang, PIDs, Zielwerte), seit 27.10.2026 statt Sperren; der Konflikt folgt eigens. */}
        <RequestWarnings board={board} item={item} warnings={mergeWarnings(item.warnings, referenceWarnings)}/>
        {/* Hinweis statt Sperre (Entscheidung David 27.10.2026): Freigeben bleibt möglich, nur GitHub kann abweisen. */}
        {item.conflicts.length > 0 && <Alert severity="warning" role="status">
          {t("stageBranches.warnings.STAGE_SHARED_RESOURCE_CONFLICT")}{" "}{t("stageBranches.page.conflictStillApprovable")}
          <Stack component="ul" spacing={0.25} sx={{ m: 0, mt: 1, pl: 2 }}>
            {item.conflicts.map(conflict => <li key={`${conflict.targetStage}:${conflict.path}`} style={{ overflowWrap: "anywhere" }}>{t("stageBranches.page.conflictPath", {
              path: conflict.path, packages: (conflict.affectedPackages || []).join(", ") })}</li>)}
          </Stack>
        </Alert>}
        {decide && <TextField multiline minRows={2} fullWidth disabled={busy} value={note} label={t("stageBranches.page.decisionNote")}
          inputProps={{ maxLength: NOTE_MAX }} helperText={t("stageBranches.page.decisionHint", { count: note.length })}
          onChange={event => setNote(event.target.value)}/>}
        <StageErrorAlert error={error} title={t("promotion.actionError")} targetName={targetNameIn(board)}/>
        {/* Unlesbare Belegung bei wartendem Start: die Wartezeile oben nennt den Grund, hier nicht ein zweites Mal. */}
        <ExecutionErrors execution={item.queue?.reason === "unreadable" && ["queued", "running"].includes(item.status)
          && item.execution?.error?.code === "STAGE_OCCUPANCY_UNREADABLE" ? { ...item.execution, error: null } : item.execution}/>
      </Stack>
    </DialogContent>
    <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
      <MonitorButton autoFocus disabled={busy} onClick={onClose}>{t("gemeinsam:aktion.schliessen")}</MonitorButton>
      {item.status !== "pending" && <CicdMonitorButton spaceId={board.spaceId} operationId={item.requestId}
        runId={firstRunIdOf(item.items, item.targets) || undefined}/>}
      {item.release && onViewRelease && <MonitorButton disabled={busy} onClick={() => onViewRelease(item)}>{t("stageBranches.page.viewRelease")}</MonitorButton>}
      {decide && <MonitorButton disabled={busy || !validNote(note)} onClick={() => run(() => onReject(item, note.trim()))}>{t("promotion.reject")}</MonitorButton>}
      {decide && <MonitorButton variant="contained" disabled={busy || note.length > NOTE_MAX}
        onClick={() => run(() => onApprove(item, note.trim()))}>
        {/* Ziel und Quelle stehen in der Route oben im Dialog; der Knopf heißt kurz „Freigeben“ (SB-F-46). */}
        {busy ? t("promotion.sending") : t("promotion.approve")}
      </MonitorButton>}
    </DialogActions>
  </MonitorDialog>;
}

/** Transportfreigabe eines Space mit eingeschalteten Stufenbranches (Vertrag 16.2, SB-F-34). */
export default function StageBranchApprovals({ spaceId, readModelEnabled = false, readModel = null }) {
  const { t } = useTranslation(["freigabe", "gemeinsam"]);
  const [topicId, setTopicId] = useState(null);
  const [regionChoice, setRegionChoice] = useState(null);
  const sb = useStageBoard(spaceId, { enabled: true, includeDev: true, topicId, region: regionChoice, readModelEnabled, readModel });
  // `board` ist auf die gewählte Region zugeschnitten (ohne Mehrzielmodus das Board selbst); `rawBoard` bleibt vollständig.
  const { board: rawBoard, view, props, region } = sb;
  const board = view;
  const pendingPackages = useMemo(() => (Array.isArray(sb.readModel?.pendingPackages) ? sb.readModel.pendingPackages : [])
    .filter(pkg => pkg.stage === "dev" && typeof pkg.packageId === "string" && typeof pkg.jobId === "string"), [sb.readModel?.pendingPackages]);
  const pendingIds = useMemo(() => new Set(pendingPackages.map(pkg => pkg.packageId)), [pendingPackages]);
  const visiblePending = pendingPackages.filter(pkg => topicId == null || (topicId === NO_TOPIC ? !pkg.topicId : pkg.topicId === topicId));
  const localReads = readModelEnabled && readModel?.localReads === true;
  const rights = board?.rights || {};
  // DEV183: „Aus der Pipeline entfernen“. Das Recht liefert GET …/stage-board/removals, nicht das Board.
  const [canRemove, setCanRemove] = useState(false);
  const [hiddenCards, setHiddenCards] = useState(() => new Set());
  const [removeDialog, setRemoveDialog] = useState(null);
  const [removeError, setRemoveError] = useState("");
  const [removing, setRemoving] = useState(false);
  const [undo, setUndo] = useState(null);
  // Optimistisches UI: Overlay verschiebt Karten sofort in die Zielspalte, bevor der Server antwortet.
  // Schluessel: packageId:targetStage (+ optional targetId); Wert enthaelt requestId fuer Aufloesung.
  const [optimistic, setOptimistic] = useState(() => new Map());
  const addOverlay = (key, targetStage, requestId) => setOptimistic(prev => new Map(prev).set(key, { targetStage, requestId, at: Date.now() }));
  const removeOverlay = key => setOptimistic(prev => { const next = new Map(prev); next.delete(key); return next; });
  // Overlay-Polling: Solange Overlays bestehen, alle 5 s das Board lesen, damit der Beleg kommt; nie, solange ein Abruf
  // läuft (whenIdle). Pausiert bei verborgenem Tab (document.visibilityState).
  useEffect(() => {
    if (!optimistic.size) return undefined;
    const poll = () => { if (document.visibilityState !== "hidden") sb.refresh({ whenIdle: true }); };
    const timer = setInterval(poll, 5000);
    return () => clearInterval(timer);
  }, [optimistic.size]); // eslint-disable-line react-hooks/exhaustive-deps
  // Board-Refresh: Overlay aufloesen, wenn der Server den passenden Antrag/Lauf bestaetigt oder ablehnt (G4).
  // Nur ein Board, dessen Abruf nach der Serverantwort der Aktion begann, darf ein Overlay auflösen oder verwerfen (I5):
  // Das Overlay entsteht vor dem POST; ein Abruf vor dem Klick oder während des laufenden POST trägt noch den Stand von
  // davor („failed“) und würde das Overlay zu Unrecht aufheben. Maßgeblich ist sb.lastAcceptedAt (Browserzeit der
  // Serverantwort); solange keine Aktion seit dem Overlay angenommen ist, löst kein Board auf (bei Fehlschlag entfernt der
  // Aufrufer das Overlay). Verfall erst nach dem längsten Lesefenster (zwei Versuche à 90 s plus Pause).
  const boardRequestedAt = sb.boardRequestedAt, lastAcceptedAt = sb.lastAcceptedAt;
  useEffect(() => {
    if (!optimistic.size || !board) return;
    const requestStatuses = new Map();
    for (const req of board.requests || []) requestStatuses.set(req.requestId, req.status);
    for (const pkg of board.packages || []) for (const open of pkg.openRequests || []) requestStatuses.set(open.requestId, open.status);
    const now = Date.now();
    setOptimistic(prev => {
      const next = new Map();
      let changed = false;
      for (const [k, val] of prev) {
        const gateAt = val.acceptedAt != null ? val.acceptedAt : null;
        if (gateAt === null || !(boardRequestedAt >= gateAt)) { next.set(k, val); continue; }
        const reqStatus = val.requestId ? requestStatuses.get(val.requestId) : null;
        // Beleg: Antrag laeuft/wartet oder Zelle bestaetigt → Overlay weg, Serverkarte zeigen.
        const running = reqStatus && ["queued", "running", "waiting", "unknown"].includes(reqStatus);
        const completed = reqStatus && ["completed", "partial"].includes(reqStatus);
        const rejected = reqStatus && ["failed", "not_started", "rejected"].includes(reqStatus);
        if (completed || rejected) { changed = true; continue; }
        // Wenn der Antrag laeuft/wartet, gehoert die Karte zum Server → Overlay weg.
        if (running) { changed = true; continue; }
        // Verfall nach dem Lesefenster: nur verwerfen, wenn kein laufender/wartender Antrag.
        if (now - (val.acceptedAt ?? val.at) >= OVERLAY_TTL_MS) { changed = true; continue; }
        next.set(k, val);
      }
      return changed ? next : prev;
    });
  }, [board, lastAcceptedAt]); // eslint-disable-line react-hooks/exhaustive-deps
  // Details-Dialog: eine Karte hat ihren eigenen Dialog.
  const [detailRowId, setDetailRowId] = useState(null);
  const loadRemovals = sb.loadRemovals;
  useEffect(() => {
    let live = true;
    setCanRemove(false);
    loadRemovals?.().then(result => { if (live) setCanRemove(result?.canRemove === true); }).catch(() => { if (live) setCanRemove(false); });
    return () => { live = false; };
  }, [spaceId, loadRemovals]);
  // Sobald das Board das Paket nicht mehr führt, braucht es die lokale Ausblendung nicht mehr; ein neuer Stand erscheint wieder.
  useEffect(() => {
    if (!board || !hiddenCards.size) return;
    const present = new Set((board.packages || []).map(pkg => pkg.packageId));
    const keep = [...hiddenCards].filter(key => present.has(key.slice(0, key.lastIndexOf(":"))));
    if (keep.length !== hiddenCards.size) setHiddenCards(new Set(keep));
  }, [board]); // eslint-disable-line react-hooks/exhaustive-deps
  const removalErrorText = failure => ["STAGE_REMOVE_FORBIDDEN", "STAGE_CARD_CHANGED"].includes(failure?.code)
    ? t(`stageBranches.removal.errors.${failure.code}`) : (failure?.message || t("stageBranches.removal.errors.unknown"));
  const confirmRemove = async () => {
    const row = removeDialog;
    if (!row || removing) return;
    const key = `${row.packageId}:${row.sourceStage}`;
    setRemoving(true); setRemoveError("");
    setHiddenCards(previous => new Set(previous).add(key));
    try {
      // Die candidateId der gezeigten Zelle belegt den Stand; Fehlerkarten haben keine (Lane C, DEV183 Review-Fixes).
      // loadedAt ist der Boardstand beim Öffnen des Dialogs (board.generatedAt), immer gesendet; candidateId zusätzlich bei
      // normalen Karten. Ohne beides antwortet der Server mit 400.
      const result = await sb.removeCard({ packageId: row.packageId, stage: row.sourceStage, ...(row.cellError ? {} : { candidateId: row.id }),
        ...(row.loadedAt ? { loadedAt: row.loadedAt } : {}) });
      setRemoveDialog(null);
      setUndo(result?.removalId ? { removalId: result.removalId, key, name: titleOf(row) } : null);
    } catch (failure) {
      setHiddenCards(previous => { const next = new Set(previous); next.delete(key); return next; });
      setRemoveError(removalErrorText(failure));
    } finally { setRemoving(false); }
  };
  const undoRemove = async () => {
    const current = undo;
    if (!current) return;
    setUndo(null);
    try {
      await sb.unremoveCards([current.removalId]);
      setHiddenCards(previous => { const next = new Set(previous); next.delete(current.key); return next; });
    } catch (failure) { setPageError(failure); }
  };
  const regions = useMemo(() => regionsOf(rawBoard), [rawBoard]);
  // Die Region merkt sich der Browser je Benutzer und Space; ein nicht lesbarer Speicher zählt als keine Wahl.
  const regionStorageKey = rights.userId ? `stageBoard.region:${rights.userId}:${spaceId}` : null;
  useEffect(() => {
    if (!regionStorageKey) return;
    try { setRegionChoice(window.localStorage.getItem(regionStorageKey) || null); } catch { setRegionChoice(null); }
  }, [regionStorageKey]);
  const changeRegion = value => {
    setRegionChoice(value); setSelection({ stage: null, ids: [] });
    if (!regionStorageKey) return;
    try { if (value) window.localStorage.setItem(regionStorageKey, value); else window.localStorage.removeItem(regionStorageKey); } catch { /* Speicher gesperrt */ }
  };
  // Antragsdialog: { kind: "compose" | "decision" | "release", ... } oder null.
  const [dialog, setDialog] = useState(null);
  const reportTrigger = useRef(null);
  const archiveTrigger = useRef(null);
  // origin (optional): der Dialog, der eine Aktion ausgelöst hat. Eine späte Schließung (die Aktion wartet bis 30 s auf das
  // Board) trifft nur ihn, nie einen inzwischen anders geöffneten Dialog. Ohne origin (onClose der Dialoge) schließt der aktuelle.
  const closeDialog = origin => {
    const own = origin && typeof origin === "object" && typeof origin.kind === "string" ? origin : null;
    const closing = own || dialog;
    if (closing?.kind === "report") requestAnimationFrame(() => reportTrigger.current?.focus());
    if (closing?.kind === "archive") requestAnimationFrame(() => archiveTrigger.current?.focus());
    setDialog(current => (own && current !== own ? current : null));
  };
  const [pageError, setPageError] = useState(null);
  const [retryingPreparation, setRetryingPreparation] = useState(null);
  const retryPreparationBusy = useRef(false);
  const [dismissingPreparation, setDismissingPreparation] = useState(null);
  const [referenceNotes, setReferenceNotes] = useState([]);
  const [success, setSuccess] = useState("");
  // Eine Auswahl (Vertrag 27.2): Karten-IDs innerhalb genau einer Spalte.
  const [selection, setSelection] = useState({ stage: null, ids: [] });
  const clearSelection = () => setSelection({ stage: null, ids: [] });
  useEffect(() => {
    if (!readModelEnabled || ![401, 403].includes(sb.readError?.status)) return;
    setDialog(null); setDetailRowId(null); setRemoveDialog(null); setCanRemove(false);
    setSelection({ stage: null, ids: [] }); setUndo(null); setOptimistic(new Map());
    setSuccess(""); setReferenceNotes([]);
  }, [readModelEnabled, sb.readError?.status]);
  const requests = useMemo(() => openRequestsOf(board, topicId), [board, topicId]);
  const preparations = sb.preparations || [];
  const preparationOf = item => item?.status !== "pending" ? undefined : preparations.filter(row => row.action === "approve" && row.requestId === item.requestId)
    .sort((a, b) => (b.updatedAt || "").localeCompare(a.updatedAt || ""))[0];
  // Failed notices end: hidden by the actor or an approver, after 24 h, or answered by a newer request on the route.
  const visiblePreparations = requestPreparationNotices(preparations, board?.requests || []).filter(row => (!topicId || (row.packageIds || []).some(id => board?.packages?.some(pkg => pkg.packageId === id && matchesTopic(pkg, topicId)))));
  const [category, setCategory] = useState("open");
  const [requestPage, setRequestPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const filteredRequests = requests.filter(item => category === "all" || requestCategory(item.status) === category);
  const page = Math.min(requestPage, Math.max(0, Math.ceil(filteredRequests.length / pageSize) - 1));
  const shownRequests = filteredRequests.slice(page * pageSize, (page + 1) * pageSize);
  const decisionItem = dialog?.kind === "decision" ? requests.find(item => item.requestId === dialog.item.requestId) || dialog.item : null;
  useEffect(() => { setCategory("open"); setRequestPage(0); }, [spaceId]);
  useEffect(() => { setRequestPage(0); }, [topicId]);
  const errorRows = useMemo(() => errorRowsOf(board, topicId, true).filter(row => row.sourceStage !== "dev" || !pendingIds.has(row.packageId)), [board, topicId, pendingIds]);

  const done = key => { setPageError(null); setReferenceNotes([]); setSuccess(t(`promotion.accepted.${key}`)); };
  const retry = async (requestId, packageIds, targets) => {
    setSuccess(""); setPageError(null);
    try { await sb.retry(requestId, packageIds, targets); done("retry"); }
    catch (failure) { setPageError(asError(failure)); }
  };
  // Wiederholen einer fehlgeschlagenen Zelle auf der Kartenstufe: Karte bleibt in der Spalte (gleiche Stufe).
  // Kein Overlay noetig (gleiche Spalte), aber Board sofort neu lesen.
  const [retryingIds, setRetryingIds] = useState(() => new Set());
  const retryOptimistic = async row => {
    if (!row.execution?.requestId) return;
    setDetailRowId(null);
    setSuccess(""); setPageError(null);
    try { await sb.retry(row.execution.requestId, [row.packageId], row.targetId ? [row.targetId] : undefined); done("retry"); sb.refresh({ whenIdle: true }); }
    catch (failure) { setPageError(asError(failure)); }
  };
  // Wiederholen des gescheiterten Antrags in die Folgestufe (forward): Overlay nach forward.stage.
  // BLOCKER 1: KEINE targets senden (row.targetId ist das Ziel der Quellstufe; Backend prueft gegen targetsOfRequest -> 400).
  const retryForward = async row => {
    if (!row.forward?.requestId) return;
    const overlayKey = `${row.packageId}:${row.forward.stage}`;
    if (row.forward.stage) { addOverlay(overlayKey, row.forward.stage, row.forward.requestId); }
    setDetailRowId(null);
    setSuccess(""); setPageError(null);
    try {
      // Annahme je Overlay im Moment der Serverantwort festhalten: nur ein Board, das danach angefordert wurde, darf genau
      // dieses Overlay auflösen (zwei gleichzeitige Wiederholungen öffnen sich nicht gegenseitig das Gate).
      await sb.retry(row.forward.requestId, [row.packageId], undefined, { onAccepted: acceptedAt => {
        if (row.forward.stage) setOptimistic(prev => { const val = prev.get(overlayKey); return val ? new Map(prev).set(overlayKey, { ...val, acceptedAt }) : prev; });
      } });
      done("retry");
    }
    catch (failure) { if (row.forward.stage) removeOverlay(overlayKey); setPageError(asError(failure)); }
  };
  const rolloutFromDialog = row => {
    setDetailRowId(null);
    openRequest([{ ...row, sourceStage: row.rollout.sourceStage, targetStage: row.rollout.targetStage }]);
  };
  // Fehler der PROD-Sperre zeigt ihr Dialog; die Seite wiederholt sie nicht.
  const saveProdLock = async input => {
    setSuccess("");
    try { await sb.setProdLock(input); }
    finally { sb.clearError(); }
  };
  // Ein Antrag startet keinen Transport (nur pending), daher kein Overlay.
  const submitRequest = async input => {
    const opened = dialog;
    setSuccess("");
    const result = await sb.request(input);
    closeDialog(opened); clearSelection();
    if (result?.preparation?.status === "preparing") setSuccess(""); else done("request");
    // DEV183: Hinweise zu fremden Referenzen aus der Antwort des Antrags, ohne Sperre.
    setReferenceNotes(requestReferenceWarnings(result));
  };
  const submitRelease = async ({ packageIds, topicId: releaseTopic, date, targets }) => {
    const opened = dialog;
    const keys = (board?.stages || []).filter(stage => stage.configured).map(stage => stage.key);
    const sourceStage = keys[keys.indexOf("prod") - 1];
    const result = await sb.request({ sourceStage, targetStage: "prod", packageIds, ...(targets ? { targets } : {}), note: t("stageBranches.page.releaseNote", { date }),
      ...(releaseTopic && releaseTopic !== NO_TOPIC ? { topicId: releaseTopic } : {}),
      release: { date, ...(releaseTopic && releaseTopic !== NO_TOPIC ? { topicId: releaseTopic } : {}) } });
    closeDialog(opened); if (result?.preparation?.status === "preparing") setSuccess(""); else done("request");
  };
  const viewRelease = async item => {
    closeDialog(); setPageError(null);
    try {
      const result = await sb.getRequest(item.requestId);
      setDialog({ kind: "release", request: result?.request || null });
    } catch (failure) { setPageError(asError(failure)); }
  };
  const refreshRelease = async request => {
    const opened = dialog;
    const date = String(request.release?.tag || "").replace(/^release\//, "");
    await sb.refreshRequest(request.id, { note: request.note || t("stageBranches.page.releaseNote", { date }) });
    closeDialog(opened); done("request");
  };

  // Antrag für die Auswahl öffnen: nach PROD über den Releasedialog (Vertrag 10, 27.2), sonst der Antragsdialog.
  const loadDetails = sb.loadDetails;
  const openRequest = rows => {
    if (!rows.length) return;
    const { sourceStage, targetStage } = rows[0];
    // Read only the selected source stage. The hook reuses exact-version facts
    // and invalidates affected package details when a newer board arrives.
    setSuccess(""); new Set(rows.map(row => row.packageId)).forEach(id => loadDetails(id, { sourceStage }));
    setDialog(targetStage === "prod" ? { kind: "release", request: null, packageIds: [...new Set(rows.map(row => row.packageId))] }
      : { kind: "compose", sourceStage, targetStage, rows });
  };
  // Paketfehler einer Stufe mit Inhalt (ohne cell.error), etwa PD_SCOPE_UNKNOWN (Vertrag 6.1, 25.1): bleibt an der Karte sichtbar.
  const stageErrorsOf = row => (board?.packages?.find(pkg => pkg.packageId === row.packageId)?.errors || [])
    .filter(entry => entry.stage === row.sourceStage && !row.cellError);
  const stageErrorText = entry => entry.code === "PD_SCOPE_UNKNOWN" ? t("stageBranches.page.pdScopeUnknown")
    : t(`stageBranches.errors.${entry.code}`, { defaultValue: t("stageBranches.page.packageError", { code: entry.code }) });
  const canChangeTopic = rights.topicChange === true;
  // DEV183: Entfernte Karten verschwinden sofort, bevor das Board neu gelesen ist.
  // Optimistisches UI: Overlay-Karten erscheinen in der Zielspalte mit laufendem Status.
  // Schluessel ist packageId:targetStage (+ targetId bei Mehrziel).
  const candidates = (props?.candidates || []).filter(row => !hiddenCards.has(`${row.packageId}:${row.sourceStage}`) && (row.sourceStage !== "dev" || !pendingIds.has(row.packageId)))
    .map(row => {
      const key = `${row.packageId}:${row.targetStage || ""}`;
      const ol = optimistic.get(key);
      return ol ? { ...row, sourceStage: ol.targetStage, stageState: "running", selectable: false, _optimistic: true } : row;
    });
  // Auswahl und Weitergabe sind getrennt (Bedienfluss DEV154): wählbar ist eine Karte, wenn sie weitergegeben werden kann
  // ODER ihr Topic geändert werden darf. So bleiben gleicher Stand, Freeze, offener Antrag, letzte Spalte und Verwalter
  // ohne Antragsrecht für „Topic ändern“ erreichbar; die Sperre prüft TopicChangeControl.
  // Karten der letzten Stufe bleiben auf dem Board, bis ein Freigebender sie archiviert (nur Webapp, nie Git).
  const canArchive = rights.canArchive === true;
  const archivable = row => !row.cellError && !row.targetStage && canArchive && row.stageState !== "running";
  const pickable = row => !row.cellError && (row.selectable === true || canChangeTopic || archivable(row));
  // Nach einem neuen Board-Stand fallen nicht mehr wählbare Karten heraus.
  const selectedRows = candidates.filter(row => pickable(row) && row.sourceStage === selection.stage && selection.ids.includes(row.id));
  // In der letzten Spalte ist die Hauptaktion „Archivieren“ statt der Weitergabe.
  const lastColumn = selectedRows.length > 0 && !selectedRows[0].targetStage;
  // Warum eine gewählte Karte nicht weitergegeben (bzw. in der letzten Spalte nicht archiviert) werden kann; null, wenn sie es kann.
  const notPromotableReason = row => {
    if (!row.targetStage) return archivable(row) ? null
      : t(canArchive ? "stageBranches.archive.running" : "stageBranches.archive.noRight");
    if (row.selectable === true) return null;
    if (row.blockedReason) return t(`promotion.blocked.${row.blockedReason}`);
    if (row.sameAsNext) return t("stageBranches.page.sameAsNext", { stage: labelOf(board, row.targetStage) });
    return t("stageBranches.selection.noRequestRight", { stage: labelOf(board, row.sourceStage) });
  };
  const blockedSelection = selectedRows.map(row => ({ row, reason: notPromotableReason(row) })).filter(entry => entry.reason);
  // Mehrzielmodus: dasselbe Paket kann je Ziel gewählt sein; gezählt und aufgelistet wird es einmal.
  const selectedPackages = uniqueRows(selectedRows);
  const blockedShown = [...new Map(blockedSelection.map(entry => [`${entry.row.packageId}:${entry.reason}`, entry])).values()];
  const [archiving, setArchiving] = useState(false);
  const archiveSelection = async () => {
    if (blockedSelection.length || archiving) return;
    const count = new Set(selectedRows.map(row => row.packageId)).size;
    setArchiving(true); setSuccess(""); setPageError(null);
    try { await sb.archive(selectedRows.map(row => row.id)); clearSelection(); setSuccess(t("stageBranches.archive.done", { count })); }
    catch (failure) { setPageError(asError(failure)); }
    finally { setArchiving(false); }
  };
  const toggleSelect = row => {
    if (!pickable(row)) return;
    setSuccess("");
    setSelection(previous => previous.stage !== row.sourceStage ? { stage: row.sourceStage, ids: [row.id] }
      : { stage: row.sourceStage, ids: previous.ids.includes(row.id) ? previous.ids.filter(id => id !== row.id) : [...previous.ids, row.id] });
  };
  // Klick nach einem Zug löst keine Auswahl aus.
  const pointerStart = useRef(null);
  const topicTargets = (board?.packages || []).filter(pkg => selectedRows.some(row => row.packageId === pkg.packageId))
    .map(pkg => ({ packageId: pkg.packageId, packageName: pkg.packageName, topicId: pkg.topicId ?? null, topicLock: pkg.topicLock }));
  // Topic-Sperre nur Hinweis (Korrekturrunde 2): Die Antwort nennt umgehängte Pakete aus einem anderen Topic.
  // Die Hinweise gehören zu genau dieser Erfolgsmeldung; eine spätere Meldung blendet sie aus.
  const [topicNotices, setTopicNotices] = useState({ success: null, rows: [] });
  const topicChanged = async result => {
    const saved = t("stageBranches.topicChange.saved");
    clearSelection(); setPageError(null); setSuccess(saved);
    setTopicNotices({ success: saved, rows: topicNoticesOf(result, board?.topics || [], t) });
    await sb.refresh();
  };
  // Ziehen nur in die nächste Spalte (Vertrag 27.4): die Karte bzw. die Auswahl ihrer Spalte; Ablegen öffnet den Antrag.
  const { dragProps, dragging, layer } = useStageDrag({
    enabled: !dialog,
    canDrag: row => row.selectable === true && !row.cellError,
    targetStage: row => row.targetStage,
    // Die ganze Auswahl nur, wenn sie vollständig weitergegeben werden kann; sonst nur die gezogene Karte.
    dragSet: row => (row.sourceStage === selection.stage && selection.ids.includes(row.id) && !blockedSelection.length
      ? selectedRows.map(item => item.id) : [row.id]),
    onDrop: ids => openRequest(candidates.filter(row => ids.includes(row.id))),
    labels: { count: n => t("promotion.dragCount", { count: n }), cancelled: t("promotion.dropInvalid") },
  });
  // HOCH 2: Antragsstatus aus board.requests lesen. Wenn der Antrag der Zelle waiting/queued/running ist,
  // zeigt die Karte "Wiederholung wartet" statt des roten Punkts und bietet kein erneutes Wiederholen.
  // MITTEL 2: Status je Paket-Item des Antrags, nicht des ganzen Antrags.
  const RETRY_ACTIVE = new Set(["queued", "running", "waiting", "unknown"]);
  const requestStatusMap = useMemo(() => {
    const m = new Map();
    for (const req of board?.requests || []) m.set(req.requestId, req.status);
    for (const pkg of board?.packages || []) for (const open of pkg.openRequests || []) m.set(open.requestId, open.status);
    return m;
  }, [board]);
  // itemStatusOf: Status des Items fuer ein bestimmtes Paket in einem Antrag.
  const itemStatusOf = (reqId, packageId) => {
    if (!reqId) return null;
    const req = (board?.requests || []).find(r => r.requestId === reqId);
    const item = req?.items?.find(it => it.packageId === packageId);
    return item?.status || req?.status || requestStatusMap.get(reqId) || null;
  };
  const isRetryActive = row => {
    const reqId = row.execution?.requestId || row.forward?.requestId;
    if (!reqId) return false;
    const status = itemStatusOf(reqId, row.packageId);
    return RETRY_ACTIVE.has(status);
  };

  // Kompakte Kartenansicht: Titel, Info-Tabelle, Status (Fortschritt / Fehlerpunkt), Icon-Fusszeile.
  // Alle aktiven Aktionen stehen im Details-Dialog (CardDetailDialog).
  const cardHead = row => {
    const metaRows = [
      { key: "art", label: t("stageBranches.cardTable.artifacts"), value: `${t("promotion.artifactCount", { count: (row.artifactIds || []).length })}${row.transport === "package" ? ` · ${t("promotion.wholePackage")}` : ""}` },
      { key: "pd", label: t("promotion.partners"), value: row.partnerDirectory ? t("stageBranches.pd.count", { count: row.partnerDirectory.entries.length }) : t("stageBranches.pd.unknown") },
      row.at && { key: "date", label: t("stageBranches.cardTable.date"), value: <DateLabel value={row.at}/> },
      row.targetStage && !row.sameAsNext && { key: "route", label: t("stageBranches.cardTable.route"), value: labelOf(board, row.targetStage) },
      row.sameAsNext && { key: "same", label: t("stageBranches.cardTable.stand"), value: t("stageBranches.page.sameAsNext", { stage: labelOf(board, row.targetStage) }) },
      row.forward && { key: "fwd", label: t("stageBranches.cardTable.forward"), value: t("stageBranches.card.forwardFailed", { stage: labelOf(board, row.forward.stage) }) },
    ];
    return <>
      <div className="promotion-card-head"><h4>{titleOf(row)}</h4></div>
      {row.packageName && row.packageName !== row.packageId && <p className="promotion-card-id">{row.packageId}</p>}
      <CardMetaTable rows={metaRows}/>
    </>;
  };
  const renderCandidate = row => {
    const isOptimistic = !!row._optimistic;
    const retryActive = isRetryActive(row);
    // NIEDRIG 3: unknown-Antrag als Aufmerksamkeitssignal statt „Transport laeuft".
    const reqId = row.execution?.requestId || row.forward?.requestId;
    const itemStatus = reqId ? itemStatusOf(reqId, row.packageId) : null;
    const retryUnknown = retryActive && itemStatus === "unknown";
    const selected = selectedRows.some(item => item.id === row.id);
    const selectable = pickable(row) && !isOptimistic;
    const draggable = row.selectable === true && !row.cellError && !isOptimistic;
    const monitorHref = row.execution && ["running", "failed", "partial", "deployment-failed"].includes(row.stageState)
      ? cicdMonitorUrl({ spaceId, runId: row.execution.runId, operationId: row.execution.requestId, packageId: row.packageId }) : null;
    // HOCH 2: Laufende Wiederholung blockiert den roten Punkt.
    const showOpenRequest = false; // nur retryActive unterdrückt den Punkt; offene Anträge nicht
    return <article className={`promotion-candidate is-compact sb-card${selected ? " is-selected" : ""}`} key={row.id}
      style={{ position: "relative", overflow: "hidden" }}
      {...(draggable ? dragProps(row) : {})} data-drag-id={row.id}>
    {/* Statuspunkt oben rechts: rot bei fehlgeschlagen (nur wenn kein Retry laeuft), orange bei Aufmerksamkeit */}
    {!row.cellError && !retryActive && (["failed", "deployment-failed"].includes(row.stageState) || row.forward) && <FailedDot t={t} label={row.stageState === "deployment-failed" ? t("stageBranches.state.deployment-failed") : undefined}/>}
    {!row.cellError && !row.forward && !retryActive && ["partial", "pending-proof"].includes(row.stageState) && <AttentionDot label={t(`stageBranches.state.${row.stageState === "partial" && row.execution?.uploadConfirmed === true && !row.edgePending ? "import-partial" : row.stageState}`)}/>}
    {/* NIEDRIG 3: Aufmerksamkeitssignal bei unknown-Antragsstatus */}
    {retryUnknown && <AttentionDot label={t("stageBranches.dispatchUnknown")}/>}
    {/* MUI ButtonBase als div erhaelt den bestehenden Ziehgriff und die Tastaturbedienung. */}
    {selectable ? <ButtonBase component="div" role="button" tabIndex={0} aria-pressed={selected} className="sb-card-select"
      aria-label={t("stageBranches.selection.select", { name: titleOf(row) })}
      onPointerDown={event => { pointerStart.current = { x: event.clientX, y: event.clientY }; }}
      onClick={event => {
        const start = pointerStart.current; pointerStart.current = null;
        if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 6) return;
        toggleSelect(row);
      }}>
      {cardHead(row)}</ButtonBase> : cardHead(row)}
    {/* Status: Fehlertext bei cellError, Fortschritt bei laufendem Transport */}
    {row.cellError ? <Typography variant="body2" sx={{ color: v("danger"), overflowWrap: "anywhere" }}>
      {t(`stageBranches.errors.${row.cellError}`, { defaultValue: row.cellError })}
    </Typography> : row.progressRequest && ["failed", "queued", "running", "unknown"].includes(row.progressRequest.status)
      ? <StageRequestProgress request={row.progressRequest}/> : retryUnknown
      ? <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: v("warning") }}>{t("stageBranches.dispatchUnknown")}</Typography>
      : retryActive
      ? <Stack spacing={0.75}><Typography component="span" variant="body2" role="status" sx={{ fontWeight: 600, color: v("accent") }}>{t("promotion.transportRunning")}</Typography>
        <LinearProgress aria-label={t("stageBranches.cell.progress")}/></Stack>
      : <StageCellState state={row.stageState} execution={row.execution} packageId={row.packageId} spaceId={spaceId} compact/>}
    {/* Icon-Fusszeile: Monitor | Details | Entfernen */}
    {/* HOCH 7 + 3c: Entfernen bei laufendem Transport, Wartestand und offenem Antrag sperren */}
    {(() => {
      const removeBlocked = row.stageState === "running" || isOptimistic || retryActive;
      const removeReason = removeBlocked ? t("stageBranches.removal.removeBlocked") : undefined;
      // Wiederholen direkt auf der Karte (gleiche Regel wie im Details-Dialog).
      const { canRetryMain, canRetryFwd } = retryActionOf(row, { board, requestStatusMap, rights, retryActive });
      const retryKind = canRetryMain ? "main" : canRetryFwd ? "fwd" : null;
      const retryLabel = retryKind === "fwd"
        ? `${t("stageBranches.cell.retry")} (${labelOf(board, row.forward.stage)})` : t("stageBranches.cell.retry");
      const onCardRetry = retryKind ? async () => {
        setRetryingIds(ids => new Set(ids).add(row.id));
        try { await (retryKind === "main" ? retryOptimistic(row) : retryForward(row)); }
        finally { setRetryingIds(ids => { const next = new Set(ids); next.delete(row.id); return next; }); }
      } : undefined;
      return <CardFooter
        onRetry={onCardRetry}
        retryLabel={retryLabel}
        retryDisabled={retryingIds.has(row.id)}
        monitorHref={monitorHref}
        onDetails={!row.cellError ? () => setDetailRowId({ id: row.id, packageId: row.packageId, targetId: row.targetId }) : undefined}
        onRemove={canRemove ? () => { setRemoveError(""); setRemoveDialog({ ...row, loadedAt: rawBoard?.generatedAt || null }); } : undefined}
        removeDisabled={removeBlocked}
        removeDisabledReason={removeReason}
        t={t}/>;
    })()}
  </article>;
  };

  const renderStageActivity = stage => {
    const stageTargets = stage?.targets || (board?.stages?.find(row => row.key === stage.key)?.allTargets || []).filter(target =>
      !stage.instanceName || target.instanceName === stage.instanceName);
    const targetIds = new Set(stageTargets.map(target => target.targetId));
    const activity = requests.filter(request => request.targetStage === stage.key
      && !["failed", "completed", "rejected", "cancelled"].includes(request.status)).flatMap(request => (request.items || []).flatMap(item => {
      const childRows = item.targets && targetIds.size ? Object.entries(item.targets).filter(([id]) => targetIds.has(id)).map(([targetId, child]) => ({ ...child, targetId }))
        : targetIds.size ? (request.targets || ["default"]).filter(id => targetIds.has(id)).map(targetId => ({ ...item, targetId })) : [item];
      return childRows.filter(child => ["queued", "running", "unknown"].includes(child.status)).map((child, index) => ({
        key: `${request.requestId}:${item.packageId}:${index}`, name: item.packageName || item.packageId, targetName: stageTargets.find(target => target.targetId === child.targetId)?.name || stageTargets.find(target => target.targetId === child.targetId)?.instanceName,
        request: { ...request, ...child, execution: child.execution || item.execution || request.execution },
      }));
    }));
    for (const request of requests.filter(row => row.targetStage === stage.key && row.status === "pending")) {
      const preparation = preparationOf(request);
      if (!["preparing", "completed"].includes(preparation?.status)) continue;
      const selectedTargets = preparation.targets || request.targets || ["default"];
      const visibleTargets = selectedTargets.filter(id => !targetIds.size || targetIds.has(id));
      for (const item of request.items || []) {
        if (Array.isArray(preparation.packageIds) && !preparation.packageIds.includes(item.packageId)) continue;
        for (const targetId of visibleTargets) activity.push({
          key: `preparation:${preparation.id}:${item.packageId}:${targetId}`, name: item.packageName || item.packageId,
          targetName: stageTargets.find(target => target.targetId === targetId)?.instanceName,
          request, preparation,
        });
      }
    }
    return activity.length ? <Stack spacing={1} sx={{ mb: 2 }}>
      {activity.map(row => <Stack key={row.key} className="sb-stage-activity" spacing={1} sx={{ p: 1.5, border: "1px solid var(--iio-border)", borderRadius: "var(--iio-r-md)" }}>
        <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>{t("stageBranches.progress.targetOrder", { name: row.name })}</Typography>
        {row.targetName && <Typography variant="body2" sx={{ color: "var(--text-2)" }}>{row.targetName}</Typography>}
        <StageRequestProgress request={row.request} preparation={row.preparation}/>
      </Stack>)}
    </Stack> : null;
  };

  return <div className="promotion-content sb-approvals" aria-busy={sb.loading}>
    {sb.loading && !board && <LinearProgress aria-label={t("gemeinsam:zustand.laedt")}/>}
    {sb.readModel && ["pending", "unavailable"].includes(sb.readModel.state) && <Alert severity={sb.readModel.state === "unavailable" ? "error" : "info"} variant="outlined" role="status"
      action={sb.readModel.state === "unavailable" ? <MonitorButton size="small" disabled={sb.loading} onClick={() => sb.refresh()}>{t("stageBranches.page.reload")}</MonitorButton> : undefined}>
      {t(`stageBranches.readModel.${sb.readModel.state === "stale" ? "stale" : sb.readModel.state === "unavailable" ? "unavailable" : "pending"}`)}
      <ReadModelEvidence metadata={sb.readModel}/>
      {sb.readModel.state === "pending" && <LinearProgress aria-label={t("gemeinsam:zustand.laedt")} sx={{ mt: 1 }}/>}
    </Alert>}
    {sb.progressError && <Alert severity="warning" action={<MonitorButton size="small" onClick={sb.refreshProgress}>{t("stageBranches.page.reload")}</MonitorButton>}>
      {t("stageBranches.progress.readFailed")}</Alert>}
    {/* Lesefehler: der zuletzt geladene Stand bleibt stehen, „Erneut laden“ ist die eine Bedienstelle (sb.refresh). Schreibknöpfe
        sind dabei gesperrt (disabled={!!sb.readError}); zusätzlich prüft der Server jede Schreibaktion gegen seinen frischen Stand
        (pinDigest, candidateId, loadedAt), ein veralteter Stand kann also nichts Falsches schreiben. */}
    <StageErrorAlert error={sb.readError} title={t("stageBranches.page.readError")} retryDisabled={!!sb.loading} onRetry={() => { setSuccess(""); sb.refresh(); }}/>
    {!dialog && <StageErrorAlert error={pageError} title={t("promotion.actionError")} targetName={targetNameIn(rawBoard)}/>}
    {success && <Alert severity="success" role="status">{success}</Alert>}
    {success && referenceNotes.length > 0 && <RequestWarnings board={board} warnings={referenceNotes}/>}
    {success && success === topicNotices.success && topicNotices.rows.length > 0 && <Alert severity="warning" variant="outlined" role="status">
      <Stack component="ul" spacing={0.25} sx={{ m: 0, pl: 2 }}>{topicNotices.rows.map((text, i) => <li key={i} style={{ overflowWrap: "anywhere" }}>{text}</li>)}</Stack>
    </Alert>}
    {visiblePending.length > 0 && <Stack component="section" spacing={1.5} aria-label={t("stageBranches.readModel.savedTitle")} sx={{ my: 2 }}>
      <Typography component="h2" variant="h6">{t("stageBranches.readModel.savedTitle")}</Typography>
      <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1.5}>
        {visiblePending.map(pkg => <Card key={`${pkg.jobId}:${pkg.packageId}`} variant="outlined" sx={{ flex: "1 1 280px", maxWidth: "100%", bgcolor: "var(--iio-bg-1)", color: "var(--iio-text-1)", borderColor: "var(--iio-border)" }}>
          <CardContent>
            <Typography component="h3" variant="subtitle1" sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>{pkg.packageName || pkg.packageId}</Typography>
            <Typography variant="body2" sx={{ color: "var(--iio-text-2)", overflowWrap: "anywhere" }}>{t("stageBranches.readModel.saved")}</Typography>
          </CardContent>
        </Card>)}
      </Stack>
    </Stack>}
    {board && props && <>
      <section className="promotion-section promotion-requests" aria-labelledby="stage-requests-title">
        <div className="promotion-section-head">
          <div className="promotion-head-copy"><h2 id="stage-requests-title">{t("promotion.requests")}</h2>
            {sb.readModel && <ReadModelInfo label={t("stageBranches.readModel.infoLabel")} message={sb.readModel.state === "stale" ? t("stageBranches.readModel.stale") : t("stageBranches.readModel.infoReady")} metadata={sb.readModel}/>}
          </div>
          <div className="promotion-head-actions">
            <MonitorButton variant="outlined" startIcon={<RefreshRounded/>} disabled={sb.loading} onClick={() => { setSuccess(""); sb.refresh(); }}>
              {t("gemeinsam:aktion.aktualisieren")}</MonitorButton>
          </div>
        </div>
        {!rights.canApprove && <p className="promotion-muted">{t("promotion.approverHint")}</p>}
        {visiblePreparations.map(preparation => <Card key={preparation.id} variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography component="h3" variant="subtitle1" sx={{ overflowWrap: "anywhere" }}>{(preparation.packageIds || []).map(id => board?.packages?.find(pkg => pkg.packageId === id)?.packageName || id).join(", ")}</Typography>
            <StageRequestProgress preparation={preparation}/>
            {preparation.status === "failed" && <BlockingRequests preparation={preparation} requests={requests}
              onOpen={item => { setSuccess(""); setDialog({ kind: "decision", item }); }}/>}
            {preparation.status === "failed" && (preparation.clientRequestId || rights.canApprove) && <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1} sx={{ mt: 1.5 }}>
              {preparation.clientRequestId && <MonitorButton
                variant="outlined" disabled={!!retryingPreparation || !!dismissingPreparation} onClick={async () => {
                  if (retryPreparationBusy.current) return;
                  retryPreparationBusy.current = true; setRetryingPreparation(preparation.id); setPageError(null);
                  try { await sb.retryPreparation(preparation.id); }
                  catch (error) { if (error?.name !== "AbortError") setPageError(asError(error)); }
                  finally { retryPreparationBusy.current = false; setRetryingPreparation(null); }
                }}>{t(retryingPreparation === preparation.id ? "stageBranches.progress.requesting" : "stageBranches.progress.retryPreparation")}</MonitorButton>}
              <Tooltip title={t("stageBranches.progress.dismissPreparationHint")}><span>
                <MonitorButton variant="text" disabled={!!retryingPreparation || !!dismissingPreparation} onClick={async () => {
                  if (dismissingPreparation) return;
                  setDismissingPreparation(preparation.id); setPageError(null);
                  try { await sb.dismissPreparation(preparation.id); }
                  catch (error) { if (error?.name !== "AbortError") setPageError(asError(error)); }
                  finally { setDismissingPreparation(null); }
                }}>{t("stageBranches.progress.dismissPreparation")}</MonitorButton>
              </span></Tooltip>
            </Stack>}
          </CardContent>
        </Card>)}
        <Tabs className="promotion-tabs" value={category} onChange={(_, value) => { setCategory(value); setRequestPage(0); }}
          variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile aria-label={t("promotion.filters")}>
          {["open", "successful", "failed", "all"].map(value => <Tab key={value} value={value} id={`sb-requests-tab-${value}`}
            aria-controls="sb-requests-panel" label={`${t(`promotion.categories.${value}`)} (${requests.filter(item => value === "all" || requestCategory(item.status) === value).length})`}/>) }
        </Tabs>
        <div id="sb-requests-panel" role="tabpanel" aria-labelledby={`sb-requests-tab-${category}`}>
        {!shownRequests.length ? <div className="promotion-empty"><p>{t("promotion.noMatchingRequests")}</p></div>
          : <div className="promotion-request-grid">{shownRequests.map(item => <ButtonBase component="button" type="button" key={item.requestId}
            className="promotion-request-card" aria-haspopup="dialog" onClick={() => { setSuccess(""); setDialog({ kind: "decision", item }); }}>
            <span className={`promotion-status promotion-status--${item.status} promotion-status-tab`}><span aria-hidden="true"/>{preparationOf(item)?.status === "completed" ? t("stageBranches.progress.confirming") : preparationOf(item)?.status === "preparing" ? t("stageBranches.progress.approving") : t(item.status === "completed" ? "promotion.categories.successful" : `promotion.status.${item.status}`)}</span>
            <span className="promotion-card-main">
              <span className="promotion-card-title">{item.release?.tag || item.packages.map(titleOf).join(", ")}</span>
              {item.release?.tag && <span className="promotion-card-preview">{item.packages.map(titleOf).join(", ")}</span>}
              <RouteText board={board} sourceStage={item.sourceStage} targetStage={item.targetStage}/>
              <RequestNote note={item.note} compact/>
              <StageRequestProgress request={item} preparation={preparationOf(item)} compact/>
            </span>
            <span className="promotion-card-action">{rights.canApprove && item.status === "pending" && !["preparing", "completed"].includes(preparationOf(item)?.status)
              ? t("freigabe.knopfFreigeben", { ziel: instanceOf(board, item.targetStage) || t("promotion.instanceMissing") }) : t("promotion.viewRequest")}</span>
          </ButtonBase>)}</div>}
        </div>
        {filteredRequests.length > 10 && <TablePagination component="div" count={filteredRequests.length} page={page} rowsPerPage={pageSize}
          rowsPerPageOptions={[10, 25, 50]} onPageChange={(_, next) => setRequestPage(next)}
          onRowsPerPageChange={event => { setPageSize(Number(event.target.value)); setRequestPage(0); }}
          labelRowsPerPage={t("report.rowsPerPage")} labelDisplayedRows={({ from, to, count }) => t("topics.pageRange", { from, to, count })}
          getItemAriaLabel={type => t(`topics.page.${type}`)}/>}
      </section>
      <section className="promotion-section" aria-labelledby="stage-board-title">
        <div className="promotion-section-head">
          <div className="promotion-head-copy sb-board-head-copy">
            {/* Archiv oben rechts in der Titelzeile; Report und Topic-Filter bleiben darunter. */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" useFlexGap flexWrap="wrap" spacing={1.5}>
              <h2 id="stage-board-title">{t("stageBranches.page.title")}</h2>
              <MonitorButton ref={archiveTrigger} className="promotion-head-button" variant="outlined" startIcon={<Inventory2Outlined/>}
                aria-haspopup="dialog" disabled={!!sb.readError} onClick={() => setDialog({ kind: "archive" })}>
                {t("stageBranches.archive.button", { count: (board.archive || []).length })}</MonitorButton>
            </Stack>
            <p className="promotion-muted">{t("stageBranches.page.hint")}</p>
            {/* H4: Der Server meldete kurz keinen Stand (z. B. gleichzeitiger Schreibvorgang); das zuletzt bestätigte Board bleibt stehen. */}
            {sb.readModel?.held && <p className="promotion-muted" role="status">{t("stageBranches.readModel.stale")}</p>}</div>
          <div className="promotion-head-actions">
            <MonitorButton ref={reportTrigger} className="promotion-head-button" variant="outlined" startIcon={<DescriptionOutlined/>}
              aria-haspopup="dialog" disabled={!!sb.readError} onClick={() => setDialog({ kind: "report", board: rawBoard })}>{t("report.button")}</MonitorButton>
            <TopicFilter topics={board.topics || []} value={topicId}
            onChange={value => { setTopicId(value); clearSelection(); }}/>
            {board.multiTarget && regions.length > 0 && <RegionFilter regions={regions} value={region} onChange={changeRegion}/>}</div>
        </div>
        <StageQueue board={board} capacity={board.capacity}/>
        <PromotionStageBoard fit stages={props.stages} candidates={[...candidates, ...errorRows]} includeDev={props.includeDev}
          renderCandidate={renderCandidate} renderStageActivity={renderStageActivity} dropTarget={dragging?.targetStage ?? null} dropOver={!!dragging?.over}/>
      </section>
      {/* Eine Leiste für die Auswahl (Vertrag 27.2): Weitergabe, „Topic ändern“ und „Auswahl aufheben“. */}
      {selectedRows.length > 0 && <PromotionSelectionBar
        title={t("promotion.selectionSummary", { packages: t("promotion.dragCount", { count: selectedPackages.length }),
          artifacts: t("promotion.artifactCount", { count: selectedPackages.reduce((sum, row) => sum + (row.artifactIds || []).length, 0) }) })}
        subtitle={<>
          {selectedRows[0].targetStage
            ? `${instanceOf(board, selectedRows[0].sourceStage) || t("promotion.instanceMissing")} (${labelOf(board, selectedRows[0].sourceStage)}) → ${instanceOf(board, selectedRows[0].targetStage) || t("promotion.instanceMissing")} (${labelOf(board, selectedRows[0].targetStage)})`
            : `${instanceOf(board, selectedRows[0].sourceStage) || t("promotion.instanceMissing")} (${labelOf(board, selectedRows[0].sourceStage)})`}
          <span className="sb-selection-note">{t("stageBranches.selection.oneColumn")}</span>
        </>}
        warning={blockedSelection.length > 0 && <>
          {t(lastColumn ? "stageBranches.archive.notArchivable" : "stageBranches.selection.notPromotable", { count: blockedShown.length })}
          <Stack component="ul" spacing={0.25} sx={{ m: 0, mt: 0.5, pl: 2 }}>
            {blockedShown.map(({ row, reason }) => <li key={row.id} style={{ overflowWrap: "anywhere" }}>
              {t("stageBranches.selection.reasonLine", { name: titleOf(row), reason })}</li>)}
          </Stack>
        </>}
        primaryLabel={lastColumn ? t(archiving ? "promotion.sending" : "stageBranches.archive.action")
          : t("stageBranches.selection.request", { stage: labelOf(board, selectedRows[0].targetStage) })}
        primaryDisabled={blockedSelection.length > 0 || archiving}
        onPrimary={() => { if (lastColumn) archiveSelection(); else if (!blockedSelection.length) openRequest(selectedRows); }} onClear={clearSelection}
        extraActions={canChangeTopic ? <TopicChangeControl spaceId={spaceId} topics={board.topics || []} packages={topicTargets} onChanged={topicChanged}/> : undefined}/>}
    </>}
    {layer}
    {removeDialog && <MonitorDialog maxWidth="sm" className="promotion-dialog sb-dialog" aria-labelledby="stage-remove-title"
      onClose={() => { if (!removing) setRemoveDialog(null); }}>
      <DialogTitle id="stage-remove-title">{t("stageBranches.removal.title")}</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5}>
          <Typography variant="body2" sx={{ color: v("text-1"), overflowWrap: "anywhere" }}>
            {t("stageBranches.removal.confirm", { name: titleOf(removeDialog), stage: labelOf(board, removeDialog.sourceStage) })}</Typography>
          {removeError && <Alert severity="error" variant="outlined" sx={{ borderColor: "var(--iio-critical)", color: "var(--iio-text-1)", backgroundColor: "transparent",
            "& .MuiAlert-icon": { color: "var(--iio-critical)" } }}>{removeError}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <MonitorButton onClick={() => setRemoveDialog(null)} disabled={removing}>{t("stageBranches.removal.cancel")}</MonitorButton>
        <MonitorButton variant="contained" disableElevation onClick={confirmRemove} disabled={removing}>
          {t(removing ? "stageBranches.removal.running" : "stageBranches.removal.action")}</MonitorButton>
      </DialogActions>
    </MonitorDialog>}
    <Snackbar open={!!undo} autoHideDuration={10000} onClose={(_event, reason) => { if (reason !== "clickaway") setUndo(null); }}
      message={undo ? t("stageBranches.removal.done", { name: undo.name }) : ""}
      // MUIs SnackbarContent invertiert das Schema (im Dunkelmodus hell); hier gelten die Flächen und Texte der App.
      ContentProps={{ className: "iio", sx: SNACKBAR_SX }}
      action={<MonitorButton variant="text" size="small" onClick={undoRemove} sx={{ color: "var(--iio-signal)", fontWeight: 600,
        "&:hover": { backgroundColor: "var(--iio-bg-2)" } }}>{t("stageBranches.removal.undo")}</MonitorButton>}/>
    {board && dialog?.kind === "archive" && <StageArchiveDialog board={board} canRestore={canArchive}
      onRestore={ids => sb.restoreArchived(ids)} onClose={closeDialog}/>}
    {board && dialog?.kind === "report" && <StageReportDialog key={spaceId} board={dialog.board} spaceId={spaceId} topicId={topicId} onClose={closeDialog}/>}
    {dialog?.kind === "compose" && <RequestDialog board={board} rawBoard={rawBoard} compose={dialog} topicId={topicId} region={region} details={sb.details} onLoadDetails={loadDetails} onSubmit={submitRequest} onClose={closeDialog} onLoadReferenceHints={sb.loadReferenceHints}/>}
    {dialog?.kind === "decision" && <DecisionDialog board={board} item={decisionItem} canApprove={rights.canApprove === true}
      details={sb.details} onLoadDetails={loadDetails} preparation={preparationOf(decisionItem)}
      referenceWarnings={referenceWarningsOfRequest(board, decisionItem)}
      onApprove={async (item, note) => { const opened = dialog; const result = await sb.approve(item.requestId, item.pinDigest, note); closeDialog(opened); if (result?.preparation?.status === "preparing") setSuccess(""); else done("approve"); }}
      onReject={async (item, reason) => { const opened = dialog; await sb.reject(item.requestId, reason); closeDialog(opened); done("reject"); }}
      onViewRelease={viewRelease} onClose={closeDialog}/>}
    <ReleaseSelectionDialog open={dialog?.kind === "release"} board={rawBoard} region={region} topicId={topicId === NO_TOPIC ? null : topicId}
      request={dialog?.kind === "release" ? dialog.request : null} initialPackageIds={dialog?.kind === "release" ? dialog.packageIds : undefined}
      onSubmit={submitRelease} onRefresh={refreshRelease} onClose={closeDialog}/>
    {/* Details-Dialog: Aktionen, Warnungen, Laeufe und Artefakte einer Karte. */}
    {detailRowId && (() => { const detailRow = currentDetailRow([...candidates, ...errorRows], detailRowId); return detailRow
      ? <CardDetailDialog readModelEnabled={readModelEnabled} row={detailRow} board={board} rawBoard={rawBoard} spaceId={spaceId} sb={sb}
          onClose={() => setDetailRowId(null)} onRetry={retryOptimistic} onRetryForward={retryForward} onRollout={rolloutFromDialog}
          saveProdLock={saveProdLock} rights={rights} labelOf={labelOf} instanceOf={instanceOf} stageErrorsOf={stageErrorsOf} stageErrorText={stageErrorText}
          retryActive={isRetryActive(detailRow)} requestStatusMap={requestStatusMap}/>
      : null; })()}
  </div>;
}
